import React, { useState, useEffect, useCallback } from "react";
import { BrowserRouter, Routes, Route, useNavigate, useLocation, useParams, Navigate } from "react-router-dom";
import axiosInstance from "./components/api/axiosInstance";
import { ConfirmProvider, useConfirm } from "./components/ConfirmModal";
import { getCtxPath } from "./config/ctxPath";
import Sidebar from "./components/Sidebar";
import TopHeader from "./components/TopHeader";
import InstallPwaBanner from "./components/InstallPwaBanner";
import DigixLeads from "./pages/DigixLeads";
import WebsiteLeads from "./pages/WebsiteLeads";
import CirclesInbox from "./pages/circles/CirclesInbox";
import CirclesAppRegistrations from "./pages/circles/CirclesAppRegistrations";
import CirclesAgents from "./pages/circles/CirclesAgents";
import CirclesProjects from "./pages/circles/CirclesProjects";
import CirclesCommissions from "./pages/circles/CirclesCommissions";
import CirclesPayouts from "./pages/circles/CirclesPayouts";
import CirclesSettings from "./pages/circles/CirclesSettings";
import { openStaffDashboard } from "./pages/circles/circlesApi";
import AddBusinessLead from "./pages/AddBusinessLead";
import AddTrainingLead from "./pages/AddTrainingLead";
import AddJobLead from "./pages/AddJobLead";
import AddInternshipLead from "./pages/AddInternshipLead";
import AddRealestateLead from "./pages/AddRealestateLead";
import AddTenant from "./pages/AddTenant";
import Users from "./pages/Users";
import AddUser from "./pages/AddEmployee";
import ViewUser from "./pages/ViewEmployee";
import EditUser from "./pages/EditEmployee";
import Accounts from "./pages/Accounts";
import Signin from "./pages/Signin";
import TenantPortal from "./portal/TenantPortal";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import MyDetails from "./pages/MyDetails";
import ChangePassword from "./pages/ChangePassword";
import Settings from "./pages/Settings";
import ViewBusinessLead from "./pages/ViewBusinessLead";
import ViewTrainingLead from "./pages/ViewTrainingLead";
import ViewTenant from "./pages/ViewTenant";
import ViewJobLead from "./pages/ViewJobLead";
import ViewInternshipLead from "./pages/ViewInternshipLead";
import ViewRealestateLead from "./pages/ViewRealestateLead";
import EditRealestateLead from "./pages/EditRealestateLead";
import ViewSocialaccount from "./pages/ViewSocialaccount";
import ViewAccounts from "./pages/ViewAccounts";
import EditBusinessLead from "./pages/EditBusinessLead";
import EditTrainingLead from "./pages/EditTrainingLead";
import EditJobLead from "./pages/EditJobLead";
import EditInternshipLead from "./pages/EditInternshipLead";
import EditTenant from "./pages/EditTenant";
import EditSocialaccount from "./pages/EditSocialaccount";
import EditAccounts from "./pages/EditAccounts";
import AddAccounts from "./pages/AddAccounts";
import AddSocialaccount from "./pages/AddSocialaccount";
import AddFollowup from "./pages/AddFollowup";
import QuoteGeneratorPage from "./pages/QuoteGeneratorPage";
import SupportContacts from "./pages/SupportContacts";
import AddSupportContact from "./pages/AddSupportContact";
import AddOfficeService from "./pages/AddOfficeService";
import AddOfficeStaff from "./pages/AddOfficeStaff";
import ViewSupportContact from "./pages/ViewSupportContact";
import ViewOfficeStaff from "./pages/ViewOfficeStaff";
import { fetchSupportContacts, fetchOfficeStaff } from "./pages/supportContactService";
import ProjectManagement from "./pages/ProjectManagement";
import UserStoriesPage from "./pages/UserStoriesPage";
import TasksPage from "./pages/TasksPage";
import GenerateProjectReceipt from "./pages/GenerateProjectReceipt";
import AddProject from "./pages/AddProject";
import EditProject from "./pages/EditProject";
import ViewProject from "./pages/ViewProject";
import ViewProjectPayment from "./pages/ViewProjectPayment";
import ViewProjectSubscription from "./pages/ViewProjectSubscription";
import EditProjectSubscription from "./pages/EditProjectSubscription";
import EditProjectPayment from "./pages/EditProjectPayment";
import AddProjectPaymentPage from "./pages/AddProjectPaymentPage";
import AddSubproject from "./pages/AddSubproject";
import EditSubproject from "./pages/EditSubproject";
import ViewSubproject from "./pages/ViewSubproject";
import EditUserStories from "./pages/EditUserStories";
import ViewUserStories from "./pages/ViewUserStories";
import AddUserStory from "./pages/AddUserStory";
import AddTask from "./pages/AddTask";
import EditTask from "./pages/EditTask";
import ViewTask from "./pages/ViewTask";
import AddSubtask from "./pages/AddSubtask";
import EditSubTask from "./pages/EditSubTask";
import ViewSubTask from "./pages/ViewSubTask";
import Dashboard from "./pages/Dashboard";
import DigitalMarketingPage from "./pages/DigitalMarketingPage";
import DMProjects from "./pages/dm/DMProjects";
import DMProjectWorkspace from "./pages/dm/DMProjectWorkspace";
import DMClientDetails from "./pages/dm/DMClientDetails";
import SetupTemplateAdmin from "./pages/dm/SetupTemplateAdmin";
import MyWork from "./pages/work/MyWork";
import WorkFollowUps from "./pages/work/WorkFollowUps";
import ReviewInbox from "./pages/work/ReviewInbox";
import TeamBoard from "./pages/work/TeamBoard";
import OrgSetup from "./pages/work/OrgSetup";
import OrgChart from "./pages/work/OrgChart";
import { WorkPlanPage, WorkProjects } from "./pages/work/WorkPlanPage";
import SocialConnections from "./pages/work/SocialConnections";
import { CommentsPage } from "./pages/work/CommentsInbox";
import WhatsAppPage from "./pages/work/WhatsAppPage";
import MessagesPage from "./pages/work/MessagesPage";
import { staffWorkApi } from "./pages/work/workApi";
import PortalNoticeBanner from "./components/PortalNoticeBanner";
import HRManagement from "./pages/HRManagement";
import FinanceManagement from "./pages/FinanceManagement";
import FinanceInvoicesPage from "./pages/finance/FinanceInvoicesPage";
import FinanceReceivablesPage from "./pages/finance/FinanceReceivablesPage";
import FinancePaymentsReceivedPage from "./pages/finance/FinancePaymentsReceivedPage";
import FinancePayablesPage from "./pages/finance/FinancePayablesPage";
import FinanceSettingsPage from "./pages/finance/FinanceSettingsPage";
import { ADMIN_FINANCE_PAGE_SECTIONS, ADMIN_FINANCE_PATHS, adminFinancePageForPath } from "./utils/financeTerms";
import AddCompanyAccount from "./pages/AddCompanyAccount";
import AddCustomerAccount from "./pages/AddCustomerAccount";
import ViewCompanyAccount from "./pages/ViewCompanyAccount";
import ViewCustomerAccount from "./pages/ViewCustomerAccount";
import EditCustomerAccount from "./pages/EditCustomerAccount";
import EditCompanyAccount from "./pages/EditCompanyAccount";
import Issues from "./pages/Issues";
import AddIssue from "./pages/AddIssue";
import ViewIssue from "./pages/ViewIssue";
import EditIssue from "./pages/EditIssue";
import AddTestCase from "./pages/AddTestCase";
import EditTestCase from "./pages/EditTestCase";
import ViewTestCase from "./pages/ViewTestCase";
import Customers from "./pages/Customers";
import LoginFixes from "./pages/LoginFixes";
import Permissions from "./pages/Permissions";
import { getCurrentUserRole, canAccessPath, getDefaultLandingPath } from "./utils/roleAccess";
import { clearAuthSessionStorage } from "./utils/clearSessionStorage";
import { getAuthItem, migrateLegacyAuthToSession } from "./utils/authStorage";
import { unwrapString } from "./utils/formatters";
import "./App.css";

const WORK_PAGE_PATHS = {
  MyWork: "/my-work",
  WorkFollowUps: "/work/follow-ups",
  ReviewInbox: "/work/review",
  TeamBoard: "/work/team",
  SocialComments: "/work/comments",
  WhatsApp: "/work/whatsapp",
  SocialMessages: "/work/messages",
  SocialConnections: "/work/connections",
  WorkPlan: "/work/projects",
};

/** Circles (Digix Leads agent network) admin pages; the staff dashboard opens in a new tab instead. */
const CIRCLES_PAGE_PATHS = {
  CirclesInbox: "/circles/inbox",
  CirclesAppRegistrations: "/circles/app-registrations",
  CirclesAgents: "/circles/agents",
  CirclesProjects: "/circles/projects",
  CirclesCommissions: "/circles/commissions",
  CirclesPayouts: "/circles/payouts",
  CirclesSettings: "/circles/settings",
};
const CIRCLES_PAGE_TITLES = {
  CirclesInbox: "Digix Leads Inbox",
  CirclesAppRegistrations: "App Registrations",
  CirclesAgents: "Circles Agents",
  CirclesProjects: "Circles Projects",
  CirclesCommissions: "Circles Commissions",
  CirclesPayouts: "Circles Payouts",
  CirclesSettings: "Circles Settings",
};

function circlesPageForPath(path) {
  const hit = Object.entries(CIRCLES_PAGE_PATHS).find(([, p]) => path === p || path.startsWith(`${p}/`));
  return hit ? hit[0] : null;
}

/** Sidebar page id of an assignments path (My Work, review inbox, team board...), else null. */
function workPageForPath(path) {
  if (path === "/my-work" || path === "/my-day") return "MyWork";
  if (path.startsWith("/work/follow-ups")) return "WorkFollowUps";
  if (path.startsWith("/work/review")) return "ReviewInbox";
  if (path.startsWith("/work/team") || path.startsWith("/work/org")) return "TeamBoard";
  if (path.startsWith("/work/calendar")) return "DigitalMarketing";
  if (path.startsWith("/work/comments")) return "SocialComments";
  if (path.startsWith("/work/whatsapp")) return "WhatsApp";
  if (path.startsWith("/work/messages")) return "SocialMessages";
  if (path.startsWith("/work/connections")) return "SocialConnections";
  if (path.startsWith("/work/projects") || path.startsWith("/work/plan")) return "WorkPlan";
  return null;
}

// Main App Component with routing logic
function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const confirm = useConfirm();

  // Helpful dev-time guard: surfaces which import is undefined
  if (process.env.NODE_ENV !== "production") {
    const maybeUndefined = {
      DigixLeads,
      AddBusinessLead,
      AddTrainingLead,
      AddInternshipLead,
      AddTenant,
      Users,
      AddUser,
      ViewUser,
      EditUser,
      Accounts,
      Signin,
      ForgotPassword,
      ResetPassword,
      MyDetails,
      ChangePassword,
      ViewBusinessLead,
      ViewTrainingLead,
      ViewTenant,
      ViewSocialaccount,
      EditBusinessLead,
      EditTrainingLead,
      EditTenant,
      EditSocialaccount,
      AddAccounts,
      ViewInternshipLead,
      AddFollowup,
      QuoteGeneratorPage,
      SupportContacts,
      FinanceManagement,
      AddSupportContact,
      ViewSupportContact,
      ProjectManagement,
      AddProject,
      EditProject,
      ViewProject,
      ViewProjectPayment,
      EditProjectPayment,
      AddProjectPaymentPage,
      Issues,
      Customers,
      Permissions
    };
    const bad = Object.entries(maybeUndefined).filter(([, v]) => !v);
    if (bad.length > 0) {
      // eslint-disable-next-line no-console
      console.error("Undefined route component imports:", bad.map(([k]) => k));
    }
  }
  
  const [isLoggedIn, setIsLoggedIn] = useState(() => {
    // A company-switch handoff replaces whatever session this portal had.
    if (new URLSearchParams(window.location.search).has("handoff")) {
      clearAuthSessionStorage();
      return false;
    }
    const token = getAuthItem("access_token");
    return !!token;
  });
  
  const [activePage, setActivePage] = useState("DigixLeads");
  const [permissionsVersion, setPermissionsVersion] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem("cloudOfficeSidebarCollapsed") === "1";
    } catch (_) {
      return false;
    }
  });
  const [bootstrapReady, setBootstrapReady] = useState(false);

  useEffect(() => {
    let active = true;
    import("./cloudOffice/loadBootstrap").then((mod) => {
      if (!active) return;
      if (!isLoggedIn) {
        mod.removeBootstrapStylesheet();
        setBootstrapReady(false);
        return;
      }
      mod.applyBootstrapStylesheet();
      setBootstrapReady(true);
    });
    return () => {
      active = false;
    };
  }, [isLoggedIn]);

  useEffect(() => {
    try {
      localStorage.setItem("cloudOfficeSidebarCollapsed", sidebarCollapsed ? "1" : "0");
    } catch (_) {}
  }, [sidebarCollapsed]);

  // Collapse control on fixed sidebar (1200+); tablets/phones use drawer
  const [sidebarCollapseAllowed, setSidebarCollapseAllowed] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia("(min-width: 1200px)").matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia("(min-width: 1200px)");
    const update = () => setSidebarCollapseAllowed(mq.matches);
    update();
    if (mq.addEventListener) {
      mq.addEventListener("change", update);
      return () => mq.removeEventListener("change", update);
    }
    mq.addListener(update);
    return () => mq.removeListener(update);
  }, []);

  const sidebarCollapsedEffective = sidebarCollapseAllowed && sidebarCollapsed;

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!sidebarOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") setSidebarOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [sidebarOpen]);
  
  const [digixLeadsActiveTab, setDigixLeadsActiveTab] = useState(() => {
    const path = window.location.pathname;
    if (path === "/tenants" || path.startsWith("/tenants/")) return "Tenants";
    if (path === "/accounts" || path.startsWith("/accounts/")) return "Accounts";
    if (path === "/realestate-leads" || path.startsWith("/realestate-leads/")) return "Realestate Leads";
    if (path === "/training-leads" || path.startsWith("/training-leads/")) return "Training Leads";
    if (path === "/job-leads" || path.startsWith("/job-leads/")) return "Job Leads";
    if (path === "/internship-leads" || path.startsWith("/internship-leads/")) return "Internship Leads";
    return "Business Leads";
  });
  const [pendingLeadType, setPendingLeadType] = useState(null); // "Training Leads" when returning from training lead edit

  const [viewLeadId, setViewLeadId] = useState(null);
  const [viewLeadType, setViewLeadType] = useState("lead");
  const [showAddFollowup, setShowAddFollowup] = useState(false);
  const [addFollowupId, setAddFollowupId] = useState(null);
  const [addFollowupType, setAddFollowupType] = useState("lead");
  const [showAddUser, setShowAddUser] = useState(false);
  const [showMyDetails, setShowMyDetails] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showAddSupportContact, setShowAddSupportContact] = useState(false);
  const [showViewSupportContact, setShowViewSupportContact] = useState(false);
  const [viewSupportContactId, setViewSupportContactId] = useState(null);
  const [showEditSupportContact, setShowEditSupportContact] = useState(false);
  const [editSupportContactId, setEditSupportContactId] = useState(null);
  const [leads, setLeads] = useState([]);
  const [realestateLeads, setRealestateLeads] = useState([]);
  const [realestateTenantId, setRealestateTenantIdState] = useState(
    () => sessionStorage.getItem("digixRealestateTenantId") || ""
  );
  const setRealestateTenantId = useCallback((id) => {
    const value = id ? String(id) : "";
    if (value) sessionStorage.setItem("digixRealestateTenantId", value);
    else sessionStorage.removeItem("digixRealestateTenantId");
    setRealestateTenantIdState(value);
  }, []);
  const [trainingLeads, setTrainingLeads] = useState([]);
  const [jobLeads, setJobLeads] = useState([]);
  const [internshipLeads, setInternshipLeads] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loadingLeads, setLoadingLeads] = useState(false);
  const [loadingJobLeads, setLoadingJobLeads] = useState(false);
  const [, setLoadingInternshipLeads] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [projectViewTitle, setProjectViewTitle] = useState(null);
  const [tenantViewTitle, setTenantViewTitle] = useState(null);
  const [supportContacts, setSupportContacts] = useState([]);
  const [supportStaff, setSupportStaff] = useState([]);
  const [loadingSupportContacts, setLoadingSupportContacts] = useState(false);
  const [supportContactActiveTab, setSupportContactActiveTab] = useState("Service");

  // Clear project / tenant view titles when navigating away
  useEffect(() => {
    const path = location.pathname;
    const isProjectView = path.match(/^\/projects\/[^/]+\/view$/) && !path.includes("subprojects");
    if (!isProjectView) setProjectViewTitle(null);
    const isTenantView =
      path.includes("/tenants") &&
      path.includes("/view") &&
      !path.includes("/social-accounts");
    if (!isTenantView) setTenantViewTitle(null);
  }, [location.pathname]);

  // Sync activePage and digixLeadsActiveTab with URL path (no storage)
  useEffect(() => {
    const path = location.pathname;
    const workPage = workPageForPath(path);
    if (workPage) {
      setActivePage(workPage);
    } else if (path === "/dashboard" || path.startsWith("/dashboard/")) {
      setActivePage("Dashboard");
    } else if (
      path === "/digital-marketing" ||
      path.startsWith("/digital-marketing/") ||
      path === "/dm-calendar" ||
      path.startsWith("/dm-calendar/")
    ) {
      setActivePage("DigitalMarketing");
    } else if (path === "/dm-projects" || path.startsWith("/dm-projects/") || path.startsWith("/dm-setup-template")) {
      setActivePage("DMProjects");
    } else if (path === "/website-leads" || path.startsWith("/website-leads/")) {
      setActivePage("WebsiteLeads");
    } else if (circlesPageForPath(path)) {
      setActivePage(circlesPageForPath(path));
    } else if (
      path === "/" ||
      path === "/business-leads" ||
      path.startsWith("/business-leads/") ||
      path === "/realestate-leads" ||
      path.startsWith("/realestate-leads/") ||
      path === "/training-leads" ||
      path.startsWith("/training-leads/") ||
      path === "/job-leads" ||
      path.startsWith("/job-leads/") ||
      path === "/internship-leads" ||
      path.startsWith("/internship-leads/")
    ) {
      setActivePage("DigixLeads");
      if (path.startsWith("/realestate-leads")) {
        setDigixLeadsActiveTab("Realestate Leads");
      } else if (path.startsWith("/training-leads")) {
        setDigixLeadsActiveTab("Training Leads");
      } else if (path.startsWith("/job-leads")) {
        setDigixLeadsActiveTab("Job Leads");
      } else if (path.startsWith("/internship-leads")) {
        setDigixLeadsActiveTab("Internship Leads");
      } else {
        setDigixLeadsActiveTab("Business Leads");
      }
    } else if (path === "/tenants" || path.startsWith("/tenants/")) {
      setActivePage("Tenants");
    } else if (path === "/accounts" || path.startsWith("/accounts/")) {
      setActivePage("Accounts");
    } else if (path.startsWith("/users/org-chart")) {
      setActivePage("OrgChart");
    } else if (path === "/users" || path.startsWith("/users/")) {
      setActivePage("Users");
    } else if (path === "/support-contacts" || path.startsWith("/support-contacts/")) {
      setActivePage("Support Contacts");
      if (path === "/support-contacts/service" || path.startsWith("/support-contacts/service/")) {
        setSupportContactActiveTab("Service");
      } else {
        setSupportContactActiveTab("Staff");
      }
    } else if (path === "/projects" || path.startsWith("/projects/")) {
      if (path.includes("/tasks/")) {
        setActivePage("Tasks");
      } else if (path.includes("/user-stories")) {
        setActivePage("UserStories");
      } else {
        setActivePage("Projects");
      }
    } else if (path === "/user-stories" || path.startsWith("/user-stories/")) {
      setActivePage("UserStories");
    } else if (path === "/tasks" || path.startsWith("/tasks/")) {
      setActivePage("Tasks");
    } else if (path === "/issues" || path.startsWith("/issues/")) {
      setActivePage("Issues");
    } else if (path === "/hr-management" || path.startsWith("/hr-management/")) {
      setActivePage("HRManagement");
    } else if (adminFinancePageForPath(path)) {
      setActivePage(adminFinancePageForPath(path));
    }
  }, [location.pathname]);

  // Clear pendingLeadType after DigixLeads has applied it (so back from training edit shows Training Leads list)
  useEffect(() => {
    if ((location.pathname === "/business-leads" || location.pathname === "/realestate-leads" || location.pathname === "/training-leads" || location.pathname === "/job-leads" || location.pathname === "/internship-leads") && pendingLeadType) {
      const t = setTimeout(() => setPendingLeadType(null), 100);
      return () => clearTimeout(t);
    }
  }, [location.pathname, pendingLeadType]);

  // Fetch courses
  const fetchCourses = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/courses');
      if (response.data && Array.isArray(response.data)) {
        setCourses(response.data);
      }
    } catch (error) {
      console.error("Error fetching courses:", error);
    }
  }, []);

  // Fetch training leads from API
  const fetchTrainingLeads = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/training-leads');
      if (response.data && Array.isArray(response.data)) {
        let followupsMap = {};
        const trainingLeadIds = response.data.map(item => {
          const lead = item.training_lead || item;
          return lead.Training_Lead_Id;
        }).filter(Boolean);

        const followupPromises = trainingLeadIds.map(async (leadId) => {
          try {
            const followupResponse = await axiosInstance.get(`/training-followups/${leadId}`);
            
            if (followupResponse.data && Array.isArray(followupResponse.data) && followupResponse.data.length > 0) {
              const latest = followupResponse.data.reduce((latest, current) => {
                if (!latest) return current;
                const latestDate = new Date(latest.Followup_Date);
                const currentDate = new Date(current.Followup_Date);
                return currentDate > latestDate ? current : latest;
              });
              if (latest) {
                return { leadId, followup: latest };
              }
            }
          } catch (followupError) {
            console.error("Error fetching followup for training lead:", followupError);
            return null;
          }
          return null;
        });

        const followupResults = await Promise.all(followupPromises);
        followupResults.forEach(result => {
          if (result && result.leadId && result.followup) {
            followupsMap[result.leadId] = result.followup;
          }
        });

        const formatFollowupDate = (dateString) => {
          if (!dateString) return "—";
          try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return "—";
            const day = String(date.getDate()).padStart(2, '0');
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const year = date.getFullYear();
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            return `${day}/${month}/${year} ${hours}:${minutes}`;
          } catch {
            return "—";
          }
        };

        const mappedTrainingLeads = response.data.map(item => {
          // Handle nested structure: { address: {...}, training_lead: {...} }
          const lead = item.training_lead || item;
          const address = item.address || {};
          const latestFollowup = followupsMap[lead.Training_Lead_Id];

          // Format dates safely
          const formatDate = (dateString) => {
            if (!dateString) return "—";
            try {
              const date = new Date(dateString);
              if (isNaN(date.getTime())) return "—";
              return date.toLocaleDateString('en-GB');
            } catch {
              return "—";
            }
          };

          return {
            id: lead.Training_Lead_Id || lead.training_lead_id || null,
            name: `${lead.First_Name || lead.first_name || ''} ${lead.Last_Name || lead.last_name || ''}`.trim() || "—",
            Courses: Array.isArray(lead.Courses) ? lead.Courses : [],
            Modes: Array.isArray(lead.Modes) ? lead.Modes : [],
            services: (() => {
              const arr = Array.isArray(lead.Courses) ? lead.Courses : [];
              const names = arr.map(c => c.Course_Name || c.course_name).filter(Boolean);
              return names.length > 0 ? names.join(", ") : (lead.Course_Name || lead.course_name || "—");
            })(),
            courseId: (() => {
              const arr = Array.isArray(lead.Courses) ? lead.Courses : [];
              const first = arr[0];
              return (first && (first.Course_Id ?? first.course_id)) ?? (lead.Course_Id || lead.course_id || null);
            })(),
            courseCode: (() => {
              const arr = Array.isArray(lead.Courses) ? lead.Courses : [];
              const codes = arr.map(c => c.Course_Code || c.course_code).filter(Boolean);
              return codes.length > 0 ? codes.join(", ") : (lead.Course_Code || lead.course_code || null);
            })(),
            courseName: (() => {
              const arr = Array.isArray(lead.Courses) ? lead.Courses : [];
              const names = arr.map(c => c.Course_Name || c.course_name).filter(Boolean);
              return names.length > 0 ? names.join(", ") : (lead.Course_Name || lead.course_name || "—");
            })(),
            phone: (lead.Country_Code && (lead.Phone || lead.phone)) ? `${lead.Country_Code}${lead.Phone || lead.phone}` : (lead.Phone || lead.phone || "—"),
            altPhone: lead.Alt_Phone || lead.alt_phone || "—",
            mode: (() => {
              const arr = Array.isArray(lead.Modes) ? lead.Modes : [];
              const names = arr.map(m => m.Mode_Name || m.mode_name).filter(Boolean);
              return names.length > 0 ? names.join(", ") : (lead.Mode || lead.mode || "—");
            })(),
            notes: (latestFollowup && latestFollowup.Remarks && latestFollowup.Remarks.trim() !== "") ? latestFollowup.Remarks : "—",
            status: lead.Status || lead.status || "—",
            statusId: lead.Status_Id || lead.status_id || null,
            nextFollowupDate: latestFollowup?.Next_Followup_Date ? formatFollowupDate(latestFollowup.Next_Followup_Date) : "—",
            source: lead.Source_Name || lead.source_name || "—",
            sourceId: lead.Source_Id || lead.source_id || null,
            isQuoteSent: "No",
            location: address.City_Name || address.city_name || "—",
            cityId: address.City_Id || address.city_id || null,
            stateName: address.State_Name || address.state_name || "—",
            stateId: address.State_Id || address.state_id || null,
            countryName: address.Country_Name || address.country_name || "—",
            countryId: address.Country_Id || address.country_id || null,
            createdAt: formatDate(lead.Created_On || lead.created_on),
            assignedToId: lead.Assigned_To ?? lead.assigned_to ?? null,
            assignedTo:
              lead.Assigned_To_Name ||
              lead.assigned_to_name ||
              ((lead.Assigned_To ?? lead.assigned_to) != null
                ? `User #${lead.Assigned_To ?? lead.assigned_to}`
                : "—"),
            companyName: lead.College_Name || lead.college_name || "—",
            domain: "—",
            email: lead.Email || lead.email || "—",
            lastFollowupDate: formatDate(lead.Updated_On || lead.updated_on),
            qualificationId: lead.Qualification_Id || lead.qualification_id || null,
            qualificationName: lead.Qualification_Name || lead.qualification_name || null,
            otherQualification: lead.Other_Qualification || lead.other_qualification || null,
            specialization: lead.Specialization || lead.specialization || null,
            batch: lead.Batch || lead.batch || "—",
            expectedFee: lead.Expected_Fee || lead.expected_fee || "—",
            yearOfPassing: lead.Year_Of_Passing || lead.year_of_passing || null,
            earliestStartDate: lead.Earliest_Start_Date || lead.earliest_start_date || null,
            countryCode: lead.Country_Code || lead.country_code || null,
            leadType: "Training"
          };
        });

        setTrainingLeads(mappedTrainingLeads);
      }
    } catch (error) {
      console.error("Error fetching training leads:", error);
      setTrainingLeads([]);
    }
  }, []);

  // Fetch job leads from API
  const fetchJobLeads = useCallback(async () => {
    setLoadingJobLeads(true);
    try {
      const response = await axiosInstance.get("/job-leads");
      let followupsResponse = null;
      try {
        followupsResponse = await axiosInstance.get("/job-leads-followups");
      } catch (followupErr) {
        console.error("Error fetching job lead followups:", followupErr);
      }
      const raw = response.data;
      const arr = Array.isArray(raw)
        ? raw
        : Array.isArray(raw?.job_leads)
          ? raw.job_leads
          : Array.isArray(raw?.data)
            ? raw.data
            : Array.isArray(raw?.results)
              ? raw.results
              : [];

      const followupsRaw = followupsResponse?.data;
      const followupsArr = Array.isArray(followupsRaw)
        ? followupsRaw
        : Array.isArray(followupsRaw?.data)
          ? followupsRaw.data
          : [];

      const followupsMap = {};
      for (const f of followupsArr) {
        const jid =
          f.Job_Lead_Id ?? f.job_lead_id ?? f.JobLead_Id ?? f.jobLead_id ?? null;
        if (jid == null) continue;

        const key = String(jid);
        const prev = followupsMap[key];
        if (!prev) {
          followupsMap[key] = f;
          continue;
        }

        const prevTime = new Date(prev.Followup_Date).getTime();
        const currTime = new Date(f.Followup_Date).getTime();
        if (!Number.isNaN(currTime) && (Number.isNaN(prevTime) || currTime > prevTime)) {
          followupsMap[key] = f;
        }
      }

      const formatFollowupDate = (dateString) => {
        if (!dateString) return "—";
        try {
          const date = new Date(dateString);
          if (isNaN(date.getTime())) return "—";
          const day = String(date.getDate()).padStart(2, "0");
          const month = String(date.getMonth() + 1).padStart(2, "0");
          const year = date.getFullYear();
          const hours = String(date.getHours()).padStart(2, "0");
          const minutes = String(date.getMinutes()).padStart(2, "0");
          return `${day}/${month}/${year} ${hours}:${minutes}`;
        } catch {
          return "—";
        }
      };

      const mapped = (arr || []).map((item) => {
        const jl = item.job_lead || item;
        const address = item.address || jl.address || {};

        const createdAt = jl.Created_On
          ? new Date(jl.Created_On).toLocaleDateString("en-GB")
          : "—";

        const countryCode = unwrapString(jl.Country_Code) || jl.Country_Code;
        const phoneNum = unwrapString(jl.Phone) || jl.Phone;
        const phone = countryCode && phoneNum ? `${countryCode}${phoneNum}` : phoneNum || "—";

        const firstName = unwrapString(jl.First_Name) || jl.First_Name || "";
        const lastName = unwrapString(jl.Last_Name) || jl.Last_Name || "";

        const jobLeadId = jl.Job_Lead_Id ?? jl.job_lead_id ?? jl.JobLead_Id ?? jl.id;
        const latestFollowup = followupsMap[String(jobLeadId)] || null;

        return {
          id: jobLeadId,
          name: `${firstName} ${lastName}`.trim() || "—",
          email: unwrapString(jl.Email) || jl.Email || "—",
          phone: phone || "—",
          companyName: unwrapString(jl.Current_Company) || jl.Current_Company || "—",
          domainId: null,
          domain: "—",
          location: address.City_Name || unwrapString(jl.Preferred_Location) || jl.Preferred_Location || "—",
          services: unwrapString(jl.Skills) || jl.Skills || "—",
          createdAt,
          assignedToId: jl.Assigned_To ?? null,
          assignedTo:
            jl.Assigned_To_Name ||
            (jl.Assigned_To != null ? `User #${jl.Assigned_To}` : "—"),
          lastFollowupDate: latestFollowup?.Followup_Date ? formatFollowupDate(latestFollowup.Followup_Date) : "—",
          notes: latestFollowup?.Remarks && String(latestFollowup.Remarks).trim() !== "" ? latestFollowup.Remarks : "—",
          nextFollowupDate: latestFollowup?.Next_Followup_Date ? formatFollowupDate(latestFollowup.Next_Followup_Date) : "—",
          status: jl.Status || "—",
          statusId: jl.Status_Id ?? jl.status_id ?? null,
          source: jl.Source_Name || jl.Source || "—",
          isQuoteSent: "No",
        };
      });

      setJobLeads(mapped);
    } catch (err) {
      console.error("Error fetching job leads:", err);
      setJobLeads([]);
    } finally {
      setLoadingJobLeads(false);
    }
  }, []);

  // Fetch internship leads from API
  const fetchInternshipLeads = useCallback(async () => {
    setLoadingInternshipLeads(true);
    try {
      const response = await axiosInstance.get("/internship-leads");
      const raw = response.data;
      const arr = Array.isArray(raw)
        ? raw
        : Array.isArray(raw?.internship_leads)
          ? raw.internship_leads
          : Array.isArray(raw?.data)
            ? raw.data
            : Array.isArray(raw?.results)
              ? raw.results
              : [];

      const formatDateOnly = (dateString) => {
        if (!dateString) return "—";
        try {
          const date = new Date(dateString);
          if (isNaN(date.getTime())) return "—";
          const day = String(date.getDate()).padStart(2, "0");
          const month = String(date.getMonth() + 1).padStart(2, "0");
          const year = date.getFullYear();
          return `${day}/${month}/${year}`;
        } catch {
          return "—";
        }
      };

      const mapped = (arr || []).map((item) => {
        const il = item.internship_lead || item;
        const address = item.address || il.address || {};

        const clean = (v) => {
          const unwrapped = unwrapString(v);
          const s = unwrapped == null ? "" : String(unwrapped).trim();
          return s.replace(/^[\\'"`]+|[\\'"`]+$/g, "");
        };

        const createdAt = il.Created_On
          ? new Date(il.Created_On).toLocaleDateString("en-GB")
          : "—";

        const availableFrom = formatDateOnly(il.Available_From);

        const firstName = clean(il.First_Name) || il.First_Name || "";
        const lastName = clean(il.Last_Name) || il.Last_Name || "";

        const countryCode = clean(il.Country_Code) || il.Country_Code;
        const phoneNum = clean(il.Phone) || il.Phone;
        const phone = countryCode && phoneNum ? `${countryCode}${phoneNum}` : phoneNum || "—";

        return {
          id: il.Internship_Lead_Id ?? il.internship_lead_id ?? il.InternshipLead_Id ?? il.id,
          name: `${firstName} ${lastName}`.trim() || "—",
          email: clean(il.Email) || il.Email || "—",
          phone: phone || "—",
          companyName: clean(il.College_Name) || il.College_Name || "—",
          domainId: null,
          domain: "—",
          location:
            clean(il.Preferred_Location) ||
            il.Preferred_Location ||
            address.City_Name ||
            address?.City_Name ||
            "—",
          services: clean(il.Skills) || il.Skills || "—",
          createdAt,
          assignedToId: il.Assigned_To ?? null,
          assignedTo:
            il.Assigned_To_Name ||
            (il.Assigned_To != null ? `User #${il.Assigned_To}` : "—"),
          // No internship-followup API provided, so we reuse the follow-up columns:
          // - "Follow-up Date" => Available_From
          // - "Follow-up Notes" => Internship_Duration
          lastFollowupDate: availableFrom,
          notes: clean(il.Internship_Duration) || il.Internship_Duration || "—",
          nextFollowupDate: "—",
          status: il.Status || il.status || "—",
          statusId: il.Status_Id ?? il.status_id ?? null,
          source: clean(il.Source_Name) || il.Source_Name || il.source_name || il.Source || il.source || "—",
          isQuoteSent: "No",
        };
      });

      setInternshipLeads(mapped);
    } catch (err) {
      console.error("Error fetching internship leads:", err);
      setInternshipLeads([]);
    } finally {
      setLoadingInternshipLeads(false);
    }
  }, []);

  // Fetch real estate leads from API
  const fetchRealestateLeads = useCallback(async () => {
    if (!realestateTenantId) {
      setRealestateLeads([]);
      return;
    }
    try {
      const response = await axiosInstance.get("/real-estate-leads", {
        params: { include_followups: true, client_id: realestateTenantId },
      });
      const raw = response.data;
      const arr = Array.isArray(raw)
        ? raw
        : Array.isArray(raw?.real_estate_leads)
          ? raw.real_estate_leads
          : Array.isArray(raw?.data)
            ? raw.data
            : Array.isArray(raw?.results)
              ? raw.results
              : [];

      const formatDateOnly = (dateString) => {
        if (!dateString) return "—";
        try {
          const date = new Date(dateString);
          if (isNaN(date.getTime())) return "—";
          const day = String(date.getDate()).padStart(2, "0");
          const month = String(date.getMonth() + 1).padStart(2, "0");
          const year = date.getFullYear();
          return `${day}/${month}/${year}`;
        } catch {
          return "—";
        }
      };

      const mapped = (arr || []).map((item) => {
        const rel = item.real_estate_lead || item;
        const followups = Array.isArray(rel.followups) ? rel.followups : [];
        // followups are returned ordered by Created_On desc, so the first is the latest
        const latestFollowup = followups[0] || null;
        const firstName = unwrapString(rel.First_Name) || rel.First_Name || "";
        const lastName = unwrapString(rel.Last_Name) || rel.Last_Name || "";
        const requirementType = unwrapString(rel.Requirement_Type) || rel.Requirement_Type || "—";
        const propertyType = unwrapString(rel.Property_Type_Name) || rel.Property_Type_Name || "—";
        const linkedName = unwrapString(rel.Customer_Name) || rel.Customer_Name || "";
        const tenantId = rel.Tenant_Id ?? rel.tenant_id ?? null;
        const isTenant = tenantId != null && String(tenantId).trim() !== "";
        return {
          id: rel.Real_Estate_Lead_Id ?? rel.real_estate_lead_id ?? rel.RealEstateLead_Id ?? rel.id,
          clientId: rel.client_id ?? rel.client_id ?? null,
          tenantId,
          tenantName: isTenant ? linkedName : "",
          // Portal/CRM → Tenant col; linked Client without portal → Customer col
          customerName: !isTenant && linkedName ? linkedName : "",
          servicePropertyName:
            unwrapString(rel.Property_Service) ||
            unwrapString(rel.Property_Service_Name) ||
            unwrapString(rel.Service_Property_Name) ||
            rel.Property_Service ||
            rel.Property_Service_Name ||
            rel.Service_Property_Name ||
            "—",
          propertyServiceLink:
            unwrapString(rel.Property_Service_Link) ||
            rel.Property_Service_Link ||
            "",
          name: `${firstName} ${lastName}`.trim() || "—",
          firstName,
          lastName,
          email: unwrapString(rel.Email) || rel.Email || "—",
          phone: unwrapString(rel.Mobile_Number) || rel.Mobile_Number || "—",
          countryCode: unwrapString(rel.Country_Code) || rel.Country_Code || "",
          companyName: "—",
          domainId: null,
          domain: "—",
          location: unwrapString(rel.Preferred_Location) || rel.Preferred_Location || "—",
          propertyLocation: unwrapString(rel.Property_Location) || rel.Property_Location || "—",
          sharingPreference: unwrapString(rel.Sharing_Preference) || rel.Sharing_Preference || "—",
          moveInDate: formatDateOnly(rel.Move_In_Date),
          requirementType,
          propertyType,
          budgetMin: rel.Budget_Min ?? rel.budget_min ?? null,
          budgetMax: rel.Budget_Max ?? rel.budget_max ?? null,
          services: `${requirementType} ${propertyType}`.trim() || "—",
          createdAt: formatDateOnly(rel.Created_On),
          createdBy:
            unwrapString(rel.Created_By_Name) ||
            rel.Created_By_Name ||
            (rel.Created_By != null ? `User #${rel.Created_By}` : "—"),
          assignedToId: rel.Assigned_To ?? null,
          assignedTo:
            unwrapString(rel.Assigned_To_Name) ||
            rel.Assigned_To_Name ||
            (rel.Assigned_To != null ? `User #${rel.Assigned_To}` : "—"),
          lastFollowupDate: latestFollowup?.Followup_Date ? formatDateOnly(latestFollowup.Followup_Date) : "—",
          notes: (latestFollowup?.Remarks && String(latestFollowup.Remarks).trim() !== "") ? latestFollowup.Remarks : "—",
          nextFollowupDate: latestFollowup?.Next_Followup_Date ? formatDateOnly(latestFollowup.Next_Followup_Date) : "—",
          latestFollowupId: latestFollowup?.Followup_Id ?? null,
          status: rel.Lead_Status || rel.Lead_Status_Name || "—",
          statusId: rel.Lead_Status_Id ?? rel.lead_status_id ?? null,
          source: unwrapString(rel.Source_Name) || rel.Source_Name || "—",
          siteVisit: !!(rel.Site_Visit ?? rel.site_visit),
          isOpportunity: !!(rel.Is_Opportunity ?? rel.is_opportunity),
          automationEnabled: !!(rel.Automation_Enabled ?? rel.automation_enabled),
          isQuoteSent: "No",
        };
      });

      setRealestateLeads(mapped);
    } catch (err) {
      console.error("Error fetching real estate leads:", err);
      setRealestateLeads([]);
    }
  }, [realestateTenantId]);

  // Fetch leads from API (Business Leads only)
  const fetchLeads = useCallback(async () => {
    setLoadingLeads(true);
    try {
      const response = await axiosInstance.get('/leads');
      if (response.data && Array.isArray(response.data)) {
        const formatFollowupDate = (dateString) => {
          if (!dateString) return "—";
          try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return "—";
            const day = String(date.getDate()).padStart(2, '0');
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const year = date.getFullYear();
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            return `${day}/${month}/${year} ${hours}:${minutes}`;
          } catch {
            return "—";
          }
        };

        const pickLatestFollowup = (followups) => {
          if (!Array.isArray(followups) || followups.length === 0) return null;
          // /leads returns followups ordered by Created_On desc; also prefer latest Followup_Date
          return followups.reduce((latest, current) => {
            if (!latest) return current;
            const latestDate = new Date(latest.Followup_Date || latest.Created_On || 0).getTime();
            const currentDate = new Date(current.Followup_Date || current.Created_On || 0).getTime();
            if (Number.isNaN(currentDate)) return latest;
            if (Number.isNaN(latestDate) || currentDate > latestDate) return current;
            return latest;
          }, null);
        };

        const mappedLeads = response.data.map(item => {
          const lead = item.lead || item;
          const address = item.address || {};
          const latestFollowup = pickLatestFollowup(item.followups);

          return {
            id: lead.Lead_Id,
            name: `${lead.First_Name || ''} ${lead.Last_Name || ''}`.trim(),
            companyName: lead.Company_Name || "—",
            domainId: lead.Domain_Id ?? null,
            domain: lead.Domain || "—",
            email: lead.Email || lead.Company_Email || "—",
            phone: lead.Phone || "—",
            countryCode: lead.Country_Code || lead.country_code || "+91",
            location: address.City_Name || "—",
            services: lead.Services && lead.Services.length > 0 ? lead.Services.join(", ") : "—",
            createdAt: lead.Created_On ? new Date(lead.Created_On).toLocaleDateString('en-GB') : "—",
            assignedToId: lead.Assigned_To ?? null,
            assignedTo:
              lead.Assigned_To_Name ||
              (lead.Assigned_To != null ? `User #${lead.Assigned_To}` : "—"),
            lastFollowupDate: latestFollowup?.Followup_Date ? formatFollowupDate(latestFollowup.Followup_Date) : "—",
            notes: (latestFollowup && latestFollowup.Remarks && latestFollowup.Remarks.trim() !== "") ? latestFollowup.Remarks : "—",
            nextFollowupDate: latestFollowup?.Next_Followup_Date ? formatFollowupDate(latestFollowup.Next_Followup_Date) : "—",
            status: lead.Status || "—",
            statusId: lead.Status_Id ?? lead.status_id ?? null,
            source: lead.Source_Name || "—",
            isQuoteSent: lead.Is_Quote_Sent ? "Yes" : "No",
            tenantId: lead.Tenant_Id ?? null,
            tenantName: lead.Tenant_Name || "",
            tenantStatus: lead.Tenant_Status || "",
            tenantDomainId: lead.Tenant_Domain_Id ?? null,
          };
        });

        setLeads(mappedLeads);
      }
    } catch (error) {
      console.error("Error fetching leads:", error);
      setLeads([]);
    } finally {
      setLoadingLeads(false);
    }
  }, []);

  // Fetch customers from API
  const fetchCustomers = useCallback(async () => {
    setLoadingCustomers(true);
    try {
      const response = await axiosInstance.get('/clients');
      if (response.data && Array.isArray(response.data)) {
        const mappedCustomers = response.data.map(item => {
          const customer = item.client || item;
          const address = item.address || {};
          // Followups come with /search-clients when searching; list load skips N+1 /followups calls.
          // Tenant results table does not show follow-up columns.
          const followups = Array.isArray(item.followups) ? item.followups : [];
          const latestFollowup = followups[0] || null;

          const formatFollowupDate = (dateString) => {
            if (!dateString) return "—";
            try {
              const date = new Date(dateString);
              if (isNaN(date.getTime())) return "—";
              const day = String(date.getDate()).padStart(2, '0');
              const month = String(date.getMonth() + 1).padStart(2, '0');
              const year = date.getFullYear();
              const hours = String(date.getHours()).padStart(2, '0');
              const minutes = String(date.getMinutes()).padStart(2, '0');
              return `${day}/${month}/${year} ${hours}:${minutes}`;
            } catch {
              return "—";
            }
          };

          return {
            id: customer.client_id,
            name: `${customer.First_Name || ''} ${customer.Last_Name || ''}`.trim(),
            customerType: customer.Customer_Type || customer.customer_type || "—",
            companyName: customer.Company_Name || "—",
            ownerBusinessCount: Number(customer.Owner_Business_Count) || 0,
            domainId: customer.Domain_Id ?? null,
            domain: customer.Domain || "—",
            email: customer.Email || "—",
            companyEmail: customer.Company_Email || "—",
            companyPhone: customer.Company_Phone || "—",
            phone: customer.Phone || "—",
            countryCode: customer.Country_Code || customer.country_code || "",
            location: (() => {
              const city = String(address.City_Name || address.city_name || "").trim();
              const country = String(address.Country_Name || address.country_name || "").trim();
              if (city && country) return `${city}, ${country}`;
              if (city) return city;
              if (country) return country;
              return "—";
            })(),
            locationShort: (() => {
              const city = String(address.City_Name || address.city_name || "").trim();
              const country = String(
                address.Country_Short_Code || address.Country_Name || address.country_name || ""
              ).trim();
              return [city, country].filter(Boolean).join(", ") || "—";
            })(),
            services: customer.Services && customer.Services.length > 0 ? customer.Services : [],
            createdAt: customer.Created_On ? new Date(customer.Created_On).toLocaleDateString('en-GB') : "—",
            createdBy:
              customer.Created_By_Name ||
              (customer.Created_By != null ? `User #${customer.Created_By}` : "—"),
            updatedBy:
              customer.Updated_By_Name ||
              (customer.Updated_By != null ? `User #${customer.Updated_By}` : "—"),
            updatedAt: customer.Updated_On
              ? new Date(customer.Updated_On).toLocaleDateString("en-GB")
              : "—",
            updatedAtMs: customer.Updated_On
              ? (() => {
                  const t = new Date(customer.Updated_On).getTime();
                  return Number.isFinite(t) ? t : 0;
                })()
              : 0,
            lastFollowupDate: latestFollowup?.Followup_Date ? formatFollowupDate(latestFollowup.Followup_Date) : "—",
            notes: (latestFollowup && latestFollowup.Remarks && latestFollowup.Remarks.trim() !== "") ? latestFollowup.Remarks : "—",
            nextFollowupDate: latestFollowup?.Next_Followup_Date ? formatFollowupDate(latestFollowup.Next_Followup_Date) : "—",
            status: customer.Status || "—",
            source: customer.Source || customer.Source_Name || "—",
            isQuoteSent: customer.Is_Quote_Sent ? "Yes" : "No",
            customerNotes: customer.Notes || customer.notes || "—"
          };
        });

        setCustomers(mappedCustomers);
      }
    } catch (error) {
      console.error("Error fetching customers:", error);
      setCustomers([]);
    } finally {
      setLoadingCustomers(false);
    }
  }, []);

  // Fetch users from API
  const fetchUsers = useCallback(async () => {
    setLoadingUsers(true);
    try {
      const response = await axiosInstance.get('/users');
      const rawData = response.data;
      const usersArray = Array.isArray(rawData)
        ? rawData
        : Array.isArray(rawData?.data)
          ? rawData.data
          : Array.isArray(rawData?.users)
            ? rawData.users
            : [];
      if (usersArray.length >= 0) {
        const mappedUsers = usersArray.map(user => {
          const rawRoles = Array.isArray(user.roles) ? user.roles : [];
          const normalizedRoles = rawRoles
            .map((r) => ({
              roleId: r.role_id ?? r.roleId ?? r.id ?? null,
              roleName: r.role_name ?? r.roleName ?? r.name ?? r.role ?? ""
            }))
            .filter((r) => r.roleId != null || (r.roleName || "").trim() !== "");
          const primaryRoleName =
            (normalizedRoles[0]?.roleName || "").trim() ||
            user.role_name ||
            user.role ||
            "";

          return {
            id: user.user_id ?? user.id,
            userId: user.user_id ?? user.id,
            firstName: user.first_name ?? user.firstName ?? "",
            lastName: user.last_name ?? user.lastName ?? "",
            email: user.email ?? "",
            companyEmail: user.company_email ?? user.companyEmail ?? "",
            // Store both designation and roles separately
            role: primaryRoleName,
            roles: normalizedRoles,
            roleId: user.role_id ?? user.roleId ?? normalizedRoles[0]?.roleId ?? null,
            designation: user.designation_name ?? user.Designation_Name ?? null,
            department: user.department ?? "",
            status: user.status ?? "",
            band: user.band ?? "",
            employeeId: user.employee_id ?? user.employeeId ?? "",
            countryCode: user.country_code ?? user.countryCode ?? "",
            phoneNumber: user.phone_number ?? user.phoneNumber ?? "",
            altNumber: user.alt_number ?? user.altNumber ?? "",
            companyName: user.company_name ?? user.companyName ?? "",
            companyLocation: user.company_location ?? user.companyLocation ?? "",
            dateOfJoining: user.doj ?? user.date_of_joining ?? user.dateOfJoining ?? "",
            dateOfLeaving: user.date_of_leaving ?? user.dateOfLeaving ?? "",
            createdBy: user.created_by ?? user.Created_By ?? null,
            createdByName: user.created_by_name ?? user.Created_By_Name ?? "",
            createdOn: user.created_on ?? user.createdOn ?? "",
            updatedBy: user.updated_by ?? user.Updated_By ?? null,
            updatedByName: user.updated_by_name ?? user.Updated_By_Name ?? "",
            updatedOn: user.updated_on ?? user.updatedOn ?? "",
            aadhaar: user.aadhar ?? user.aadhaar ?? user.Aadhar ?? user.Aadhaar ?? user.aadhar_number ?? "",
            pan: user.pan ?? user.pan_number ?? user.PAN ?? "",
            address: user.address ?? ""
          };
        });
        setUsers(mappedUsers);
      }
    } catch (error) {
      console.error("Error fetching users:", error.response?.data?.message || error.response?.data?.error || error.message || error);
      setUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  }, []);

  // Fetch accounts from API
  const fetchAccounts = useCallback(async () => {
    setLoadingAccounts(true);
    try {
      const response = await axiosInstance.get("/accounts", { params: { scope: "office" } });

      const raw = response?.data;
      const accountsData = Array.isArray(raw)
        ? raw
        : Array.isArray(raw?.accounts)
          ? raw.accounts
          : Array.isArray(raw?.data)
            ? raw.data
            : raw?.Account_Id || raw?.account_id
              ? [raw]
              : raw?.data?.Account_Id || raw?.data?.account_id
                ? [raw.data]
                : [];

      const mappedAccounts = (accountsData || []).map((account) => ({
        id: account.Account_Id || account.account_id,
        account_id: account.Account_Id || account.account_id,
        accountName: account.Account_Name || account.account_name || "—",
        account_name: account.Account_Name || account.account_name || "—",
        username: account.Username || account.username || "—",
        password: account.Password || account.password || null,
        businessUrl: account.Business_URL || account.business_url || account.URL || account.url || null,
        business_url: account.Business_URL || account.business_url || account.URL || account.url || null,
        profileUrl: account.Profile_URL || account.profile_url || account.URL || account.url || "—",
        profile_url: account.Profile_URL || account.profile_url || account.URL || account.url || "—",
        countryCode: account.Country_Code || account.country_code || null,
        contact: account.Contact_Number || account.contact_number || "—",
        contact_name: account.Contact_Number || account.contact_number || "—",
        contactPhone: account.Contact_Number || account.contact_number || "—",
        contact_phone: account.Contact_Number || account.contact_number || "—",
        customerName: account.Customer_Name || account.customer_name || "—",
        customer_name: account.Customer_Name || account.customer_name || "—",
        customerId: account.client_id || account.client_id || null,
        client_id: account.client_id || account.client_id || null,
        emailUsed: account.Email_Used || account.email_used || "—",
        email_used: account.Email_Used || account.email_used || "—",
        notes: account.Notes || account.notes || "—",
        platform: account.Platform_Name || account.platform_name || "—",
        status: account.Status || account.status || "Inactive"
      }));
      setAccounts(mappedAccounts);
    } catch (error) {
      const msg = error.response?.data?.message || error.response?.data?.error || error.message || "Error fetching accounts.";
      console.error("Error fetching accounts:", msg);
      setAccounts([]);
    } finally {
      setLoadingAccounts(false);
    }
  }, []);

  // Fetch support contacts (office services) and staff from API
  const fetchSupportContactsData = useCallback(async () => {
    setLoadingSupportContacts(true);
    try {
      const [services, staff] = await Promise.all([
        fetchSupportContacts(),
        fetchOfficeStaff()
      ]);
      setSupportContacts(services || []);
      setSupportStaff(staff || []);
    } catch (err) {
      console.error("Error fetching support data:", err);
      setSupportContacts([]);
      setSupportStaff([]);
    } finally {
      setLoadingSupportContacts(false);
    }
  }, []);

  // Fetch projects from API
  // - All projects:        GET /projects
  // - By client only:    GET /projects/client/:id
  // - With search filters: GET /search-projects?status_id=&client_id=&name=&category_id=
  const fetchProjects = useCallback(async ({ customerId, status, name, categoryId } = {}) => {
    setLoadingProjects(true);
    try {
      let response;
      const baseParams = { page: 1, limit: 1000 };
      // If any NON-customer filters are provided, use search-projects endpoint
      if (status || name || categoryId) {
        const params = { ...baseParams };
        if (status) params.status = status;
        if (name) params.name = name;
        if (categoryId) params.category_id = categoryId;
        if (customerId) params.client_id = customerId;
        response = await axiosInstance.get("/search-projects", { params });
      } else {
        const url = customerId ? `/projects/client/${customerId}` : "/projects";
        response = await axiosInstance.get(url, { params: baseParams });
      }
      const raw = response.data;
      const arr = Array.isArray(raw)
        ? raw
        : Array.isArray(raw?.projects)
          ? raw.projects
          : Array.isArray(raw?.data)
            ? raw.data
            : Array.isArray(raw?.results)
              ? raw.results
              : [];
      const mapped = (arr || []).map((p) => ({
        Project_Id: p.Project_Id ?? p.project_id ?? p.Id ?? p.id,
        project_id: p.Project_Id ?? p.project_id ?? p.Id ?? p.id,
        Project_Name: p.Project_Name ?? p.project_name ?? p.name ?? "",
        project_name: p.Project_Name ?? p.project_name ?? p.name ?? "",
        Short_Code: p.Short_Code ?? p.short_code,
        short_code: p.Short_Code ?? p.short_code,
        Description: p.Description ?? p.description,
        Domain: p.Domain ?? p.domain,
        domain: p.Domain ?? p.domain,
        Domain_Id: p.Domain_Id ?? p.domain_id ?? null,
        domain_id: p.Domain_Id ?? p.domain_id ?? null,
        Category: p.Category ?? p.category,
        category: p.Category ?? p.category,
        Category_Id: p.Category_Id ?? p.category_id ?? null,
        category_id: p.Category_Id ?? p.category_id ?? null,
        Start_Date: p.Start_Date ?? p.start_date,
        start_date: p.Start_Date ?? p.start_date,
        End_Date: p.End_Date ?? p.end_date,
        end_date: p.End_Date ?? p.end_date,
        Actual_Start_Date: p.Actual_Start_Date ?? p.actual_start_date ?? null,
        actual_start_date: p.Actual_Start_Date ?? p.actual_start_date ?? null,
        Priority_Seq: p.Priority_Seq ?? p.priority_seq ?? p.PrioritySeq ?? p.prioritySeq ?? null,
        priority_seq: p.Priority_Seq ?? p.priority_seq ?? p.PrioritySeq ?? p.prioritySeq ?? null,
        Total_Cost: p.Total_Cost ?? p.total_cost ?? null,
        total_cost: p.Total_Cost ?? p.total_cost ?? null,
        Currency_Id: p.Currency_Id ?? p.currency_id ?? null,
        currency_id: p.Currency_Id ?? p.currency_id ?? null,
        Currency_Code: p.Currency_Code ?? p.currency_code ?? null,
        currency_code: p.Currency_Code ?? p.currency_code ?? null,
        Currency_Name: p.Currency_Name ?? p.currency_name ?? null,
        currency_name: p.Currency_Name ?? p.currency_name ?? null,
        Currency_Symbol: p.Currency_Symbol ?? p.currency_symbol ?? null,
        currency_symbol: p.Currency_Symbol ?? p.currency_symbol ?? null,
        Actual_End_Date: p.Actual_End_Date ?? p.actual_end_date ?? p.Actual_Date ?? p.actual_date ?? null,
        actual_end_date: p.Actual_End_Date ?? p.actual_end_date ?? p.Actual_Date ?? p.actual_date ?? null,
        Actual_Date: p.Actual_End_Date ?? p.actual_end_date ?? p.Actual_Date ?? p.actual_date ?? null,
        actual_date: p.Actual_End_Date ?? p.actual_end_date ?? p.Actual_Date ?? p.actual_date ?? null,
        Status: p.Status ?? p.status,
        status: p.Status ?? p.status,
        Status_Id: p.Status_Id ?? p.status_id ?? null,
        status_id: p.Status_Id ?? p.status_id ?? null,
        Notes: p.Notes ?? p.notes ?? null,
        notes: p.Notes ?? p.notes ?? null,
        Visibility: p.Visibility ?? p.visibility,
        visibility: p.Visibility ?? p.visibility,
        client_id: p.client_id ?? p.client_id,
        Customer_Name: p.Customer_Name ?? p.customer_name,
        Customer_name: p.Customer_Name ?? p.customer_name,
        Users: p.Users ?? p.users ?? []
      }));
      setProjects(mapped);
    } catch (err) {
      console.error("Error fetching projects:", err);
      setProjects([]);
    } finally {
      setLoadingProjects(false);
    }
  }, []);

  /** Stable reference so Projects list mount effect does not re-run on every App render (would keep loading spinner stuck). */
  const fetchProjectsWithFilters = useCallback(
    (filters) => fetchProjects(filters || {}),
    [fetchProjects]
  );

  // Fetch users when navigating to users page
  useEffect(() => {
    if (
      isLoggedIn &&
      !location.pathname.startsWith("/users/org-chart") &&
      (location.pathname === "/users" || location.pathname.startsWith("/users/"))
    ) {
      fetchUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, isLoggedIn]);

  // Fetch projects for PMS list pages and project sub-routes (edit/view/add/etc.).
  useEffect(() => {
    if (!isLoggedIn) return;
    const path = location.pathname;
    if (path === "/projects") return; // ProjectManagement fetches on mount
    if (
      path.startsWith("/projects/") ||
      path === "/user-stories" ||
      path.startsWith("/user-stories/") ||
      path === "/tasks" ||
      path.startsWith("/tasks/")
    ) {
      fetchProjects();
    }
  }, [location.pathname, isLoggedIn, fetchProjects]);

  // Route-based data fetch - only fetch APIs for the current DigixLeads tab (not all at once)
  useEffect(() => {
    if (!isLoggedIn) return;
    const path = location.pathname;

    // DigixLeads tabs - fetch only the data for the active tab
    if (path === "/" || path === "/business-leads" || path.startsWith("/business-leads/")) {
      fetchLeads();
      fetchCourses();
    } else if (path === "/training-leads" || path.startsWith("/training-leads/")) {
      fetchTrainingLeads();
      fetchCourses();
    } else if (path === "/job-leads" || path.startsWith("/job-leads/")) {
      fetchJobLeads();
      fetchCourses();
    } else if (path === "/internship-leads" || path.startsWith("/internship-leads/")) {
      fetchInternshipLeads();
      fetchCourses();
    } else if (path === "/realestate-leads" || path.startsWith("/realestate-leads/")) {
      fetchRealestateLeads();
      fetchCourses();
    } else if (path === "/tenants" || path.startsWith("/tenants/")) {
      fetchCustomers();
    } else if (path === "/accounts" || path.startsWith("/accounts/")) {
      fetchAccounts();
    } else if (path === "/support-contacts" || path.startsWith("/support-contacts/")) {
      fetchSupportContactsData();
    }
  }, [location.pathname, isLoggedIn, fetchLeads, fetchRealestateLeads, fetchTrainingLeads, fetchJobLeads, fetchInternshipLeads, fetchCustomers, fetchCourses, fetchAccounts, fetchSupportContactsData]);

  const handleLogin = () => {
    setIsLoggedIn(true);
    const role = getCurrentUserRole();
    navigate(getDefaultLandingPath(role));
  };

  const handleLogout = () => {
    clearAuthSessionStorage();
    sessionStorage.clear();
    import("./cloudOffice/loadBootstrap")
      .then((mod) => mod.removeBootstrapStylesheet())
      .finally(() => {
        setIsLoggedIn(false);
        navigate("/", { replace: true });
      });
  };

  const handleMyDetails = () => {
    navigate("/my-details");
  };

  const handleChangePassword = () => {
    navigate("/change-password");
  };

  const handleSettings = () => {
    navigate("/settings");
  };

  const handlePermissions = () => {
    navigate("/permissions");
  };

  const handlePermissionsUpdated = () => {
    setPermissionsVersion((v) => v + 1);
  };

  const handleBackToDashboard = () => {
    navigate("/dashboard");
  };

  const handleAddLeadClick = (tab = "lead", leadType = null) => {
    // Handle the case where tab is now "Business Leads", "Training Leads", "Tenants", or "Accounts"
    // Priority: Check for customer first, then training lead, then accounts, then default to business lead
    const isCustomer = tab === "Tenants" || tab === "client";

    if (isCustomer) {
      navigate("/tenants/add");
    } else if (tab === "Realestate Leads" || leadType === "Realestate Leads") {
      navigate("/realestate-leads/add");
    } else if (tab === "Training Leads" || leadType === "Training Leads") {
      navigate("/training-leads/add");
    } else if (tab === "Job Leads" || leadType === "Job Leads") {
      navigate("/job-leads/add");
    } else if (tab === "Internship Leads" || leadType === "Internship Leads") {
      navigate("/internship-leads/add");
    } else if (tab === "Accounts" || tab === "account") {
      navigate("/accounts/add");
    } else {
      navigate("/business-leads/add");
    }
  };

  const handleViewLeadClick = (id, type = "lead", opts = {}) => {
    if (type === "client") {
      navigate(`/tenants/${id}/view${opts.view === "person" ? "?view=person" : ""}`);
    } else if (type === "realestate-lead") {
      navigate(`/realestate-leads/${id}/view`);
    } else if (type === "training-lead") {
      navigate(`/training-leads/${id}/view`);
    } else if (type === "job-lead") {
      navigate(`/job-leads/${id}/view`);
    } else if (type === "internship-lead") {
      navigate(`/internship-leads/${id}/view`);
    } else if (type === "account") {
      navigate(`/accounts/${id}/view`);
    } else {
      navigate(`/business-leads/${id}/view`);
    }
  };

  const handleEditLeadClick = (leadId, type = "lead") => {
    if (type === "client") {
      navigate(`/tenants/${leadId}/edit`);
    } else if (type === "realestate-lead") {
      navigate(`/realestate-leads/${leadId}/edit`);
    } else if (type === "training-lead") {
      navigate(`/training-leads/${leadId}/edit`);
    } else if (type === "job-lead") {
      navigate(`/job-leads/${leadId}/edit`);
    } else if (type === "internship-lead") {
      navigate(`/internship-leads/${leadId}/edit`);
    } else if (type === "account") {
      navigate(`/accounts/${leadId}/edit`);
    } else {
      navigate(`/business-leads/${leadId}/edit`);
    }
  };

  const handleBackToLeads = () => {
    if (digixLeadsActiveTab === "Tenants") {
      navigate("/tenants");
    } else if (digixLeadsActiveTab === "Realestate Leads") {
      navigate("/realestate-leads");
    } else if (digixLeadsActiveTab === "Training Leads") {
      navigate("/training-leads");
    } else if (digixLeadsActiveTab === "Job Leads") {
      navigate("/job-leads");
    } else if (digixLeadsActiveTab === "Internship Leads") {
      navigate("/internship-leads");
    } else if (digixLeadsActiveTab === "Accounts") {
      navigate("/accounts");
    } else {
      navigate("/business-leads");
    }
  };

  const handleBackToUsers = () => {
    navigate("/users");
  };

  const handleViewUser = (userId) => {
    navigate(`/users/${userId}/view`);
  };

  const handleEditUser = (userId) => {
    navigate(`/users/${userId}/edit`);
  };

  const handleBackFromViewUser = () => {
    navigate("/users");
  };

  const handleBackFromEditUser = () => {
    navigate("/users");
  };

  const handleUpdateUser = async () => {
    // Refresh users list from API after updating
    await fetchUsers();
  };

  const handleDeleteUser = async (userId) => {
    const ok = await confirm({
      title: "Deactivate User",
      message: "Are you sure you want to deactivate this user? This action cannot be undone.",
    });
    if (!ok) return;

    try {
      const response = await axiosInstance.delete(`/user/${userId}`);
      if (response.data && (response.data.status === "success" || response.status === 200)) {
        alert(response.data.message || "User deactivated successfully");
        // Refresh users list
        await fetchUsers();
      }
    } catch (error) {
      console.error("Error deactivating user:", error);
      const errorMsg = error.response?.data?.message || 
                      error.response?.data?.error || 
                      "Failed to deactivate user. Please try again.";
      alert(`Error: ${errorMsg}`);
    }
  };

  const handleBackFromViewLead = () => {
    if (location.pathname.includes("/tenants/")) {
      navigate("/tenants");
      return;
    }
    navigate(-1);
  };

  const handleBackFromEditLead = () => {
    if (location.pathname.includes("/training-leads")) {
      setPendingLeadType("Training Leads");
      navigate("/training-leads");
    } else if (location.pathname.includes("/realestate-leads")) {
      navigate("/realestate-leads");
    } else if (location.pathname.includes("/business-leads")) {
      navigate("/business-leads");
    } else if (location.pathname.includes("/job-leads")) {
      navigate("/job-leads");
    } else if (location.pathname.includes("/internship-leads")) {
      setPendingLeadType("Internship Leads");
      navigate("/internship-leads");
    } else {
      navigate("/");
    }
  };

  const handleBackFromEditTenant = () => {
    navigate(-1);
  };

  const handleBackFromEditAccount = () => {
    navigate("/tenants");
  };

  const handleBackFromViewAccount = () => {
    navigate("/tenants");
  };

  const handleAddProjectClick = () => navigate("/projects/add");
  const handleViewProject = (id) => navigate(`/projects/${id}/view`);
  const handleEditProjectClick = (id) => navigate(`/projects/${id}/edit`);
  const handleBackFromViewProject = () =>
    navigate("/projects", { state: { projectManagementTab: "Projects" } });
  const handleBackFromEditProject = () =>
    navigate("/projects", { state: { projectManagementTab: "Projects" } });
  const handleUpdateProject = () => fetchProjects();

  const handleAddFollowupClick = (id, type = "lead") => {
    if (type === "client") {
      navigate(`/followups/add?client_id=${id}`);
    } else if (type === "training-lead") {
      navigate(`/followups/add?training_lead_id=${id}`);
    } else if (type === "realestate-lead") {
      navigate(`/followups/add?realestate_lead_id=${id}`);
    } else {
      navigate(`/followups/add?lead_id=${id}`);
    }
  };

  const handleBackFromAddFollowup = () => {
    navigate(-1);
  };

  const handleUpdateLead = () => {
    fetchLeads();
    fetchRealestateLeads();
    fetchTrainingLeads();
    fetchJobLeads();
    fetchInternshipLeads();
    fetchCustomers();
    fetchAccounts();
    // Navigation is handled by onBack() called by the edit page after this
  };

  const handleAddLead = () => {
    fetchLeads();
    fetchRealestateLeads();
    fetchTrainingLeads();
    fetchJobLeads();
    fetchInternshipLeads();
    fetchCustomers();
    fetchAccounts();
    handleBackToLeads();
  };

  // After adding a new customer, refresh related data and always return to Customers list
  const handleAddTenant = () => {
    fetchCustomers();
    fetchAccounts();
    navigate("/tenants");
  };

  const handleDeleteTenant = async (customerId) => {
    const ok = await confirm({
      title: "Deactivate Client",
      message: "Are you sure you want to delete this client? This will set their status to Inactive.",
    });
    if (!ok) return;

    try {
      const response = await axiosInstance.delete(`/client/${customerId}/`);
      if (response.data && response.data.message) {
        alert(response.data.message);
        fetchCustomers();
      }
    } catch (error) {
      console.error("Error deleting customer:", error);
      const errorMsg = error.response?.data?.message || error.response?.data?.error || "Failed to delete client.";
      alert(errorMsg);
    }
  };

  const handleDeleteTrainingLead = async (trainingLeadId) => {
    const ok = await confirm({
      title: "Delete Training Lead",
      message: "Are you sure you want to delete this training lead?",
    });
    if (!ok) return;

    try {
      const response = await axiosInstance.delete(`/training-lead/${trainingLeadId}`);
      if (response.data && response.data.message) {
        alert(response.data.message);
        fetchTrainingLeads();
      }
    } catch (error) {
      console.error("Error deleting training lead:", error);
      const errorMsg = error.response?.data?.message || error.response?.data?.error || "Failed to delete training lead.";
      alert(errorMsg);
    }
  };

  const handleDeleteAccount = async (accountId) => {
    const ok = await confirm({
      title: "Deactivate Account",
      message: "Are you sure you want to delete this account? This will set its status to Inactive.",
    });
    if (!ok) return;

    try {
      let response;
      try {
        // Standalone Accounts module deactivation endpoint.
        response = await axiosInstance.delete(`/delete-account/${accountId}`);
      } catch {
        // Backward-compatible fallback.
        response = await axiosInstance.delete(`/accounts/${accountId}`);
      }
      if (response.data && response.data.message) {
        alert(response.data.message);
        fetchAccounts();
      } else {
        alert("Account deactivated successfully");
        fetchAccounts();
      }
    } catch (error) {
      console.error("Error deleting account:", error);
      const errorMsg = error.response?.data?.message || error.response?.data?.error || "Failed to delete account.";
      alert(errorMsg);
    }
  };

  const handleAddUser = async (newUser) => {
    // Refresh users list from API after adding a new user
    await fetchUsers();
  };

  // One login page at / (and /login). Domain only routes the API — no Cloud Connect UI.
  // Logged-in staff must NOT treat "/" as auth — it is DigixLeads home.
  const publicAuthPages = ["/", "/login", "/forgot-password", "/reset-password"];
  const staffAuthPages = ["/login", "/forgot-password", "/reset-password"];

  // Logged out: keep users on / for login (same page for staff + tenant)
  useEffect(() => {
    if (!isLoggedIn && !publicAuthPages.includes(location.pathname)) {
      navigate("/", { replace: true });
    }
  }, [isLoggedIn, location.pathname, navigate]);

  // Optional alias: /login → / when logged out
  useEffect(() => {
    if (!isLoggedIn && location.pathname === "/login") {
      navigate("/", { replace: true });
    }
  }, [isLoggedIn, location.pathname, navigate]);

  // Redirect logged-in staff away from staff auth pages to default page (role-based)
  useEffect(() => {
    if (isLoggedIn && staffAuthPages.includes(location.pathname)) {
      const role = getCurrentUserRole();
      navigate(getDefaultLandingPath(role), { replace: true });
    }
  }, [isLoggedIn, location.pathname, navigate]);

  // Role-based route protection: redirect if current path is not allowed
  useEffect(() => {
    if (!isLoggedIn) return;
    const role = getCurrentUserRole();
    if (!canAccessPath(location.pathname, role)) {
      navigate(getDefaultLandingPath(role), { replace: true });
    }
  }, [isLoggedIn, location.pathname, navigate]);

  const getPageTitle = () => {
    const path = location.pathname;
    if (path === "/dashboard") return "Dashboard";
    if (path === "/my-work" || path === "/my-day") return "My Work";
    if (path.startsWith("/work/follow-ups")) return "Follow-ups";
    if (path.startsWith("/work/review")) return "Review Inbox";
    if (path.startsWith("/work/team")) return "Team Board";
    if (path.startsWith("/work/org")) return "Reporting Hierarchy";
    if (path.startsWith("/work/calendar")) return "Posting Calendar";
    if (path.startsWith("/work/connections")) return "API Connections";
    if (path.startsWith("/work/comments")) return "Comments";
    if (path.startsWith("/work/whatsapp")) return "WhatsApp";
    if (path.startsWith("/work/messages")) return "Messages";
    if (path.startsWith("/work/projects") || path.startsWith("/work/plan")) return "Plan & Assign";
    if (path === "/digital-marketing" || path.startsWith("/digital-marketing/") || path === "/dm-calendar" || path.startsWith("/dm-calendar/")) {
      return "Digital Marketing Calendar";
    }
    if (path === "/dm-projects") return "Marketing Profiles";
    if (path.startsWith("/dm-projects/")) return "Digital Marketing Project";
    if (path.startsWith("/dm-setup-template")) return "DM Setup Template";
    if (path === "/website-leads" || path.startsWith("/website-leads/")) return "Website Leads";
    if (circlesPageForPath(path)) return CIRCLES_PAGE_TITLES[circlesPageForPath(path)];
    if (path === "/hr-management" || path.startsWith("/hr-management/")) return "Ai Hire";
    if (path === "/finance-management/accounts/new") return "New Company Account";
    if (path === "/finance-management/payable/customer-account/new") return "New Client Account";
    if (path.includes("/finance-management/payable/customer-account/") && path.includes("/view")) return "Client Account Details";
    if (path.includes("/finance-management/payable/customer-account/") && path.includes("/edit")) return "Edit Client Account";
    if (path.includes("/finance-management/accounts/") && path.includes("/view")) return "Company Account Details";
    if (path.includes("/finance-management/accounts/") && path.includes("/edit")) return "Edit Company Account";
    if (adminFinancePageForPath(path)) return ADMIN_FINANCE_PAGE_SECTIONS[adminFinancePageForPath(path)].label;
    if (path === "/my-details") return "My Details";
    if (path === "/change-password") return "Change Password";
    if (path === "/settings") return "Settings";
    if (path === "/permissions") return "Permissions";
    if (path.startsWith("/users/org-chart")) return "Org Chart";
    if (path.includes("/users/") && path.includes("/view")) return "Employee Details";
    if (path.includes("/tenants") && path.includes("/social-accounts") && path.includes("/view")) return "Social Account Details";
    if (path.includes("/accounts") && path.includes("/view")) return "Account Details";
    if (path.includes("/support-contacts") && path.includes("/view")) return "Contact Details";
    if (path.includes("/tenants") && path.includes("/view") && !path.includes("/social-accounts")) {
      if (typeof tenantViewTitle === "string" && tenantViewTitle.trim()) return tenantViewTitle.trim();
      return "Client Details";
    }
    if (path.includes("/tenants") && path.includes("/social-accounts") && path.includes("/add")) {
      const t = location.state?.tenantTitle;
      if (t?.name) {
        const bits = [t.name];
        if (t.id != null && t.id !== "") bits.push(`#${t.id}`);
        return `Add Social Account · ${bits.join(" ")}`;
      }
      if (typeof tenantViewTitle === "string" && tenantViewTitle.trim()) {
        return `Add Social Account · ${tenantViewTitle.trim()}`;
      }
      return "Add Social Account";
    }
    if (path.includes("/training-leads") && path.includes("/view")) return "Training Lead Details";
    if (path.includes("/business-leads") && path.includes("/view")) return "Business Lead Details";
    if (path.includes("/job-leads") && path.includes("/view")) return "Job Lead Details";
    if (path.includes("/internship-leads") && path.includes("/view")) return "Internship Lead Details";
    if (path.includes("/realestate-leads") && path.includes("/view")) return "Real Estate Lead Details";
    if (path.includes("/subtasks/") && path.includes("/view")) return "Subtask Details";
    if (path.includes("/tasks/") && path.includes("/view")) return "Task Details";
    if (path.includes("/user-stories") && path.includes("/view")) return "User Story Details";
    if (path.includes("/projects") && path.includes("/subprojects") && path.includes("/view")) return "Subproject Details";
    if (path.match(/^\/projects\/[^/]+\/view$/)) {
      if (typeof projectViewTitle === "string" && projectViewTitle.trim()) return projectViewTitle.trim();
      return "Project Details";
    }
    if (path.includes("/projects") && path.includes("/view")) return "Project Details";
    if (path.includes("/issues/test-cases") && path.includes("/view")) return "Test Case Details";
    if (path.includes("/issues/") && path.includes("/view")) return "Issue Details";
    if (path.includes("/customers") && path.includes("/view")) return "Customer Details";
    if (path.includes("/view")) return "Details";
    if (path.includes("/edit")) {
      if (path.includes("/issues/test-cases")) return "Edit Test Case";
      if (path.includes("/issues")) return "Edit Issue";
      if (path.includes("/users")) return "Edit Employee";
      if (path.includes("/tenants") && path.includes("/social-accounts")) return "Edit Social Account";
      if (path.includes("/accounts")) return "Edit Account";
      if (path.includes("/tenants")) return "Edit Client";
      if (path.includes("/support-contacts/service/") && path.includes("/edit")) return "Edit Office Service";
      if (path.includes("/support-contacts")) return "Edit Contact";
      if (path.includes("/training-leads")) return "Edit Training Lead";
      if (path.includes("/business-leads")) return "Edit Business Lead";
      if (path.includes("/job-leads")) return "Edit Job Lead";
      if (path.includes("/realestate-leads")) return "Edit Real Estate Lead";
      if (path.includes("/internship-leads")) return "Edit Internship Lead";
      if (path.includes("/subtasks/") && path.includes("/edit")) return "Edit Subtask";
      if (path.includes("/tasks/") && path.includes("/edit")) return "Edit Task";
      if (path.includes("/user-stories")) return "Edit User Story";
      if (path.includes("/projects") && path.includes("/subprojects")) return "Edit Subproject";
      if (path.includes("/projects")) return "Edit Project";
      if (path.includes("/customers")) return "Edit Customer";
      return "Edit";
    }
    if (path.includes("/add")) {
      if (path.includes("/subtasks/add")) return "Add Subtask";
      if (path.includes("/tasks/add")) return "Add Task";
      if (path.includes("/user-stories/add")) return "Add User Story";
      if (path.includes("/issues/test-cases")) return "Add Test Case";
      if (path.includes("/issues")) return "Add Issue";
      if (path.includes("/tenants") && path.includes("/social-accounts")) return "Add Social Account";
      if (path.includes("/tenants")) return "New Client";
      if (path.includes("/users")) return "Add Employee";
      if (path.includes("/support-contacts/service/add")) return "Create Office Service";
      if (path.includes("/support-contacts")) return "New Contact";
      if (path.includes("/training-leads")) return "New Training Lead";
      if (path.includes("/business-leads")) return "New Business Lead";
      if (path.includes("/job-leads")) return "New Job Lead";
      if (path.includes("/realestate-leads")) return "New Real Estate Lead";
      if (path.includes("/internship-leads")) return "New Internship Lead";
      if (path.includes("/accounts")) return "New Account";
      if (path.includes("/payments/add")) return "Add Payment";
      if (path.includes("/projects") && path.includes("/subprojects")) return "Add Subproject";
      if (path.includes("/projects")) return "New Project";
      if (path.includes("/customers")) return "New Customer";
      return "New";
    }
    if (path === "/tenants" || path.startsWith("/tenants/")) return "Clients";
    if (path === "/accounts") return "Vault";
    if (path.startsWith("/accounts/")) return "Accounts";
    if (path === "/customers" || path.startsWith("/customers/")) return "Customers";
    if (
      path === "/" ||
      path.startsWith("/business-leads") ||
      path.startsWith("/training-leads") ||
      path.startsWith("/job-leads") ||
      path.startsWith("/internship-leads") ||
      path.startsWith("/realestate-leads")
    ) {
      return "Client Leads Search";
    }
    if (path === "/users") return "Employees";
    if (path === "/support-contacts" || path.startsWith("/support-contacts/")) return "Support";
    if (path === "/projects/generate-receipt") return "Generate Receipt";
    if (path === "/user-stories" || path.startsWith("/user-stories")) return "User Stories";
    if (path === "/tasks" || path.startsWith("/tasks")) return "Tasks";
    if (path === "/projects" || path.startsWith("/projects")) return "Projects";
    if (path === "/issues" || path.startsWith("/issues")) return "Issues";
    if (path === "/generate-quote") return "Generate Quote";
    if (path === "/login") return "Sign in";
    if (path === "/forgot-password") return "Forgot password";
    if (path === "/reset-password") return "Reset password";
    return null;
  };

  const path = location.pathname;
  const sidebarActivePage = path.startsWith("/my-details") || path.startsWith("/change-password") || path.startsWith("/permissions") || path.startsWith("/settings")
    ? null
    : workPageForPath(path)
      ? workPageForPath(path)
    : path === "/dashboard" || path.startsWith("/dashboard/")
      ? "Dashboard"
      : path === "/digital-marketing" || path.startsWith("/digital-marketing/") || path === "/dm-calendar" || path.startsWith("/dm-calendar/")
        ? "DigitalMarketing"
    : path.startsWith("/dm-projects") || path.startsWith("/dm-setup-template")
      ? "DMProjects"
    : path.startsWith("/website-leads")
      ? "WebsiteLeads"
    : circlesPageForPath(path)
      ? circlesPageForPath(path)
    : path === "/" || path.startsWith("/business-leads") || path.startsWith("/training-leads") || path.startsWith("/job-leads") || path.startsWith("/internship-leads") || path.startsWith("/realestate-leads")
      ? "DigixLeads"
      : path.startsWith("/tenants")
        ? "Tenants"
        : path.startsWith("/accounts")
          ? "Accounts"
          : path.startsWith("/users/org-chart")
            ? "OrgChart"
          : path.startsWith("/users")
            ? "Users"
            : path.startsWith("/support-contacts")
              ? "Support Contacts"
              : path.startsWith("/user-stories")
                ? "UserStories"
              : path.startsWith("/tasks")
                ? "Tasks"
              : path.startsWith("/projects") && path.includes("/tasks/")
                ? "Tasks"
              : path.startsWith("/projects") && path.includes("/user-stories")
                ? "UserStories"
              : path.startsWith("/projects")
                ? "Projects"
                : path.startsWith("/issues")
                  ? "Issues"
                  : path.startsWith("/hr-management")
                    ? "HRManagement"
                    : adminFinancePageForPath(path)
                      ? adminFinancePageForPath(path)
                  : activePage;

  const pageTitle = getPageTitle();
  useEffect(() => {
    const label = typeof pageTitle === "string" && pageTitle.trim() ? pageTitle.trim() : "";
    document.title = label ? `${label} · Cloud Office` : "Cloud Office";
  }, [pageTitle]);

  if (!isLoggedIn) {
    if (location.pathname === "/" || location.pathname === "/login") {
      return <Signin onLogin={handleLogin} />;
    }
    if (location.pathname === "/forgot-password") {
      return <ForgotPassword />;
    }
    if (location.pathname === "/reset-password") {
      return <ResetPassword />;
    }
    // useEffect redirects to /
    return null;
  }

  return (
    <div className={`cloud-office-app app${sidebarOpen ? " sidebar-open" : ""}${sidebarCollapsedEffective ? " sidebar-collapsed" : ""}${bootstrapReady ? " bootstrap-ready" : ""}`}>
      {sidebarOpen && (
        <button
          type="button"
          className="sidebar-backdrop"
          aria-label="Close navigation menu"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      <Sidebar
        key={permissionsVersion}
        activePage={sidebarActivePage}
        isOpen={sidebarOpen}
        collapsed={sidebarCollapsedEffective}
        onToggleCollapse={sidebarCollapseAllowed ? () => setSidebarCollapsed((c) => !c) : undefined}
        onLogout={handleLogout}
        onMyDetails={() => {
          setSidebarOpen(false);
          handleMyDetails();
        }}
        onChangePassword={() => {
          setSidebarOpen(false);
          handleChangePassword();
        }}
        onPermissions={() => {
          setSidebarOpen(false);
          handlePermissions();
        }}
        onSettings={() => {
          setSidebarOpen(false);
          handleSettings();
        }}
        onNavigate={(page) => {
        setSidebarOpen(false);
        if (page === "CirclesDashboard") {
          openStaffDashboard();
          return;
        }
        setActivePage(page);
        if (page === "Dashboard") navigate("/dashboard");
        else if (CIRCLES_PAGE_PATHS[page]) navigate(CIRCLES_PAGE_PATHS[page]);
        else if (WORK_PAGE_PATHS[page]) navigate(WORK_PAGE_PATHS[page]);
        else if (page === "DigitalMarketing") navigate("/digital-marketing");
        else if (page === "DMProjects") navigate("/dm-projects");
        else if (page === "DMSetupTemplate") navigate("/dm-setup-template");
        else if (page === "WebsiteLeads") navigate("/website-leads");
        else if (page === "DigixLeads") {
          const path = location.pathname;
          if (path.startsWith("/training-leads")) navigate("/training-leads");
          else if (path.startsWith("/job-leads")) navigate("/job-leads");
          else if (path.startsWith("/internship-leads")) navigate("/internship-leads");
          else navigate("/business-leads");
        } else if (page === "Users") navigate("/users");
        else if (page === "OrgChart") navigate("/users/org-chart");
        else if (page === "Support Contacts") navigate("/support-contacts/service");
        else if (page === "Projects") navigate("/projects");
        else if (page === "UserStories") navigate("/user-stories");
        else if (page === "Tasks") navigate("/tasks");
        else if (page === "Issues") navigate("/issues");
        else if (page === "Tenants") navigate("/tenants");
        else if (page === "Accounts") navigate("/accounts");
        else if (page === "HRManagement") navigate("/hr-management");
        else if (ADMIN_FINANCE_PATHS[page]) navigate(ADMIN_FINANCE_PATHS[page]);
      }} />
      <TopHeader
        onToggleSidebar={() => setSidebarOpen((open) => !open)}
      />
      <main className="main-content">
        <PortalNoticeBanner />
        <div className="container-fluid main-content-inner px-0 px-md-2">
        <Routes>
          {/* Main Pages */}
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/my-day" element={<Navigate to="/my-work" replace />} />
          <Route path="/my-work" element={<MyWork api={staffWorkApi} />} />
          <Route path="/work/follow-ups" element={<WorkFollowUps api={staffWorkApi} />} />
          <Route path="/work/review" element={<ReviewInbox api={staffWorkApi} />} />
          <Route path="/work/team" element={<TeamBoard api={staffWorkApi} />} />
          <Route path="/work/org" element={<OrgSetup api={staffWorkApi} />} />
          <Route path="/work/calendar" element={<Navigate to="/digital-marketing" replace />} />
          <Route path="/work/projects" element={<WorkProjects api={staffWorkApi} />} />
          <Route path="/work/plan/:ptype/:id" element={<WorkPlanPage api={staffWorkApi} />} />
          <Route path="/work/connections" element={<SocialConnections api={staffWorkApi} />} />
          <Route path="/work/comments" element={<CommentsPage api={staffWorkApi} />} />
          <Route path="/work/whatsapp" element={<WhatsAppPage api={staffWorkApi} />} />
          <Route path="/work/messages" element={<MessagesPage api={staffWorkApi} />} />
          <Route path="/digital-marketing" element={<DigitalMarketingPage />} />
          <Route path="/dm-calendar" element={<Navigate to="/digital-marketing" replace />} />
          <Route path="/dm-projects" element={<DMProjects />} />
          <Route path="/dm-projects/client/:tenantId" element={<DMClientDetails />} />
          <Route path="/dm-setup-template" element={<SetupTemplateAdmin />} />
          <Route path="/dm-projects/:id" element={<DMProjectWorkspace />} />
          <Route path="/website-leads" element={<WebsiteLeads />} />
          <Route path="/circles" element={<Navigate to="/circles/inbox" replace />} />
          <Route path="/circles/inbox" element={<CirclesInbox />} />
          <Route path="/circles/app-registrations" element={<CirclesAppRegistrations />} />
          <Route path="/circles/agents" element={<CirclesAgents />} />
          <Route path="/circles/projects" element={<CirclesProjects />} />
          <Route path="/circles/commissions" element={<CirclesCommissions />} />
          <Route path="/circles/payouts" element={<CirclesPayouts />} />
          <Route path="/circles/settings" element={<CirclesSettings />} />
          <Route path="/hr-management" element={<HRManagement />} />
          <Route path="/finance" element={<Navigate to={ADMIN_FINANCE_PATHS.FinanceInvoices} replace />} />
          <Route path={ADMIN_FINANCE_PATHS.FinanceInvoices} element={<FinanceInvoicesPage />} />
          <Route path={ADMIN_FINANCE_PATHS.FinanceReceivables} element={<FinanceReceivablesPage />} />
          <Route path={ADMIN_FINANCE_PATHS.FinancePaymentsReceived} element={<FinancePaymentsReceivedPage />} />
          <Route path={ADMIN_FINANCE_PATHS.FinancePayables} element={<FinancePayablesPage />} />
          <Route path={ADMIN_FINANCE_PATHS.FinanceSettings} element={<FinanceSettingsPage />} />
          <Route path="/finance-management" element={<FinanceManagement />} />
          <Route path="/finance-management/accounts/new" element={<AddCompanyAccount />} />
          <Route path="/finance-management/payable/customer-account/new" element={<AddCustomerAccount />} />
          <Route path="/finance-management/payable/customer-account/:id/view" element={<ViewCustomerAccount />} />
          <Route path="/finance-management/payable/customer-account/:id/edit" element={<EditCustomerAccount />} />
          <Route path="/finance-management/accounts/:id/view" element={<ViewCompanyAccount />} />
          <Route path="/finance-management/accounts/:id/edit" element={<EditCompanyAccount />} />
          <Route path="/" element={
            <DigixLeads 
              leads={leads} 
              realestateLeads={realestateLeads}
              trainingLeads={trainingLeads}
              jobLeads={jobLeads}
              internshipLeads={internshipLeads}
              courses={courses}
              onAddLeadClick={handleAddLeadClick} 
              onViewLead={handleViewLeadClick} 
              onEditLead={handleEditLeadClick} 
              onDeleteTrainingLead={handleDeleteTrainingLead}
              initialActiveTab={digixLeadsActiveTab} 
              initialLeadType={pendingLeadType}
              onTabChange={setDigixLeadsActiveTab}
              onRefreshLeads={fetchLeads}
              onRefreshTrainingLeads={fetchTrainingLeads}
              onRefreshJobLeads={fetchJobLeads}
              onRefreshInternshipLeads={fetchInternshipLeads}
              onRefreshRealestateLeads={fetchRealestateLeads}
              realestateTenantId={realestateTenantId}
              onRealestateTenantChange={setRealestateTenantId} 
            />
          } />
          <Route path="/business-leads" element={
            <DigixLeads 
              leads={leads} 
              realestateLeads={realestateLeads}
              trainingLeads={trainingLeads}
              jobLeads={jobLeads}
              internshipLeads={internshipLeads}
              courses={courses}
              onAddLeadClick={handleAddLeadClick} 
              onViewLead={handleViewLeadClick} 
              onEditLead={handleEditLeadClick} 
              onDeleteTrainingLead={handleDeleteTrainingLead}
              initialActiveTab="Business Leads" 
              initialLeadType={pendingLeadType}
              onTabChange={setDigixLeadsActiveTab}
              onRefreshLeads={fetchLeads}
              onRefreshTrainingLeads={fetchTrainingLeads}
              onRefreshJobLeads={fetchJobLeads}
              onRefreshInternshipLeads={fetchInternshipLeads}
              onRefreshRealestateLeads={fetchRealestateLeads}
              realestateTenantId={realestateTenantId}
              onRealestateTenantChange={setRealestateTenantId} 
            />
          } />
          <Route path="/realestate-leads" element={
            <DigixLeads 
              leads={leads} 
              realestateLeads={realestateLeads}
              trainingLeads={trainingLeads}
              jobLeads={jobLeads}
              internshipLeads={internshipLeads}
              courses={courses}
              onAddLeadClick={handleAddLeadClick} 
              onViewLead={handleViewLeadClick} 
              onEditLead={handleEditLeadClick} 
              onDeleteTrainingLead={handleDeleteTrainingLead}
              initialActiveTab="Realestate Leads" 
              initialLeadType={pendingLeadType}
              onTabChange={setDigixLeadsActiveTab}
              onRefreshLeads={fetchLeads}
              onRefreshTrainingLeads={fetchTrainingLeads}
              onRefreshJobLeads={fetchJobLeads}
              onRefreshInternshipLeads={fetchInternshipLeads}
              onRefreshRealestateLeads={fetchRealestateLeads}
              realestateTenantId={realestateTenantId}
              onRealestateTenantChange={setRealestateTenantId} 
            />
          } />
          <Route path="/training-leads" element={
            <DigixLeads 
              leads={leads} 
              realestateLeads={realestateLeads}
              trainingLeads={trainingLeads}
              jobLeads={jobLeads}
              internshipLeads={internshipLeads}
              courses={courses}
              onAddLeadClick={handleAddLeadClick} 
              onViewLead={handleViewLeadClick} 
              onEditLead={handleEditLeadClick} 
              onDeleteTrainingLead={handleDeleteTrainingLead}
              initialActiveTab="Training Leads" 
              initialLeadType={pendingLeadType}
              onTabChange={setDigixLeadsActiveTab}
              onRefreshLeads={fetchLeads}
              onRefreshTrainingLeads={fetchTrainingLeads}
              onRefreshJobLeads={fetchJobLeads}
              onRefreshInternshipLeads={fetchInternshipLeads}
              onRefreshRealestateLeads={fetchRealestateLeads}
              realestateTenantId={realestateTenantId}
              onRealestateTenantChange={setRealestateTenantId} 
            />
          } />
          <Route path="/job-leads" element={
            <DigixLeads 
              leads={leads} 
              realestateLeads={realestateLeads}
              trainingLeads={trainingLeads}
              jobLeads={jobLeads}
              internshipLeads={internshipLeads}
              courses={courses}
              onAddLeadClick={handleAddLeadClick} 
              onViewLead={handleViewLeadClick} 
              onEditLead={handleEditLeadClick} 
              onDeleteTrainingLead={handleDeleteTrainingLead}
              initialActiveTab="Job Leads" 
              initialLeadType={pendingLeadType}
              onTabChange={setDigixLeadsActiveTab}
              onRefreshLeads={fetchLeads}
              onRefreshTrainingLeads={fetchTrainingLeads}
              onRefreshJobLeads={fetchJobLeads}
              onRefreshInternshipLeads={fetchInternshipLeads}
              onRefreshRealestateLeads={fetchRealestateLeads}
              realestateTenantId={realestateTenantId}
              onRealestateTenantChange={setRealestateTenantId} 
            />
          } />
          <Route path="/internship-leads" element={
            <DigixLeads 
              leads={leads} 
              realestateLeads={realestateLeads}
              trainingLeads={trainingLeads}
              jobLeads={jobLeads}
              internshipLeads={internshipLeads}
              courses={courses}
              onAddLeadClick={handleAddLeadClick} 
              onViewLead={handleViewLeadClick} 
              onEditLead={handleEditLeadClick} 
              onDeleteTrainingLead={handleDeleteTrainingLead}
              initialActiveTab="Internship Leads" 
              initialLeadType={pendingLeadType}
              onTabChange={setDigixLeadsActiveTab}
              onRefreshLeads={fetchLeads}
              onRefreshTrainingLeads={fetchTrainingLeads}
              onRefreshJobLeads={fetchJobLeads}
              onRefreshInternshipLeads={fetchInternshipLeads}
              onRefreshRealestateLeads={fetchRealestateLeads}
              realestateTenantId={realestateTenantId}
              onRealestateTenantChange={setRealestateTenantId} 
            />
          } />
          <Route path="/tenants" element={
            <Customers
              customers={customers}
              onViewLead={handleViewLeadClick}
              onEditLead={handleEditLeadClick}
              onDeleteTenant={handleDeleteTenant}
              onAddTenantClick={() => navigate("/tenants/add")}
              onLoginFixesClick={() => navigate("/tenants/login-fixes")}
            />
          } />
          <Route path="/tenants/login-fixes" element={<LoginFixes onBack={() => navigate("/tenants")} />} />
          {/* Legacy /clients bookmarks → /tenants */}
          <Route path="/clients/*" element={<Navigate to={location.pathname.replace(/^\/clients/, "/tenants") + location.search} replace />} />
          <Route path="/clients" element={<Navigate to="/tenants" replace />} />
          <Route path="/accounts" element={
            <Accounts
              accounts={accounts}
              onAddAccountClick={() => navigate("/accounts/add")}
              onViewAccount={(id) => navigate(`/accounts/${id}/view`, { state: { returnTo: "/accounts" } })}
              onEditAccount={(id) => navigate(`/accounts/${id}/edit`, { state: { returnTo: "/accounts" } })}
              onDeleteAccount={handleDeleteAccount}
            />
          } />
          <Route path="/users" element={
            <Users 
              users={users} 
              loading={loadingUsers} 
              onAddUserClick={() => navigate("/users/add")}
              onViewUser={handleViewUser}
              onEditUser={handleEditUser}
              onDeleteUser={handleDeleteUser}
            />
          } />
          <Route path="/support-contacts" element={<Navigate to="/support-contacts/service" replace />} />
          <Route path="/support-contacts/staff" element={
            <SupportContacts
              supportContacts={supportContacts}
              supportStaff={supportStaff}
              onRefresh={fetchSupportContactsData}
              onAddContact={(type) => (type === "Service" ? navigate("/support-contacts/service/add") : navigate("/support-contacts/staff/add"))}
              onViewContact={(id, type) => navigate(type === "staff" ? `/support-contacts/staff/${id}/view` : `/support-contacts/${id}/view`)}
              onEditContact={(id) => navigate(`/support-contacts/staff/${id}/edit`)}
              initialActiveTab="Staff"
              onTabChange={setSupportContactActiveTab}
            />
          } />
          <Route path="/support-contacts/service" element={
            <SupportContacts
              supportContacts={supportContacts}
              supportStaff={supportStaff}
              onRefresh={fetchSupportContactsData}
              onAddContact={(type) => (type === "Service" ? navigate("/support-contacts/service/add") : navigate("/support-contacts/staff/add"))}
              onViewContact={(id) => navigate(`/support-contacts/${id}/view`)}
              onEditContact={(id) => navigate(`/support-contacts/service/${id}/edit`)}
              initialActiveTab="Service"
              onTabChange={setSupportContactActiveTab}
            />
          } />
          <Route path="/projects" element={
            <ProjectManagement
              projects={projects}
              loading={loadingProjects}
              onAddProjectClick={handleAddProjectClick}
              onViewProject={handleViewProject}
              onEditProject={handleEditProjectClick}
              onFetchProjects={fetchProjectsWithFilters}
            />
          } />
          <Route path="/user-stories" element={
            <UserStoriesPage
              projects={projects}
              loading={loadingProjects}
              onFetchProjects={fetchProjectsWithFilters}
            />
          } />
          <Route path="/tasks" element={
            <TasksPage
              projects={projects}
              loading={loadingProjects}
              onFetchProjects={fetchProjectsWithFilters}
            />
          } />
          <Route path="/projects/generate-receipt" element={<GenerateProjectReceipt />} />
          <Route path="/projects/add" element={
            <AddProject onBack={handleBackFromEditProject} onAddProject={handleUpdateProject} />
          } />
          <Route path="/projects/:id/view" element={
            <ViewProjectWrapper onBack={handleBackFromViewProject} onEditProject={handleEditProjectClick} onPageTitleReady={setProjectViewTitle} />
          } />
          <Route path="/project-subscriptions/:id/view" element={<ViewProjectSubscriptionWrapper />} />
          <Route path="/project-subscriptions/:id/edit" element={<EditProjectSubscriptionWrapper />} />
          <Route path="/project-payments/:id/view" element={<ViewProjectPaymentWrapper />} />
          <Route path="/project-payments/:id/edit" element={<EditProjectPaymentWrapper />} />
          <Route path="/projects/:id/payments/add" element={<AddProjectPaymentPageWrapper />} />
          <Route path="/projects/:id/edit" element={
            <EditProjectWrapper onBack={handleBackFromEditProject} onUpdateProject={handleUpdateProject} />
          } />
          <Route path="/projects/:id/subprojects/add" element={
            <AddSubprojectWrapper navigate={navigate} />
          } />
          <Route path="/projects/:id/subprojects/:subprojectId/view" element={
            <ViewSubprojectWrapper navigate={navigate} />
          } />
          <Route path="/projects/:id/subprojects/:subprojectId/edit" element={
            <EditSubprojectWrapper navigate={navigate} fetchProjects={fetchProjects} />
          } />
          <Route path="/projects/user-stories/add" element={<AddUserStory onBack={() => navigate(-1)} />} />
          <Route path="/projects/:id/subprojects/:subprojectId/user-stories/add" element={<AddUserStory onBack={() => navigate(-1)} />} />
          <Route path="/projects/:id/user-stories/add" element={<AddUserStory onBack={() => navigate(-1)} />} />
          <Route path="/projects/:id/user-stories/:userStoryId/view" element={<ViewUserStoriesWrapper />} />
          <Route path="/projects/:id/subprojects/:subprojectId/user-stories/:userStoryId/view" element={<ViewUserStoriesWrapper />} />
          <Route path="/projects/:id/user-stories/:userStoryId/edit" element={<EditUserStoriesWrapper />} />
          <Route path="/projects/:id/subprojects/:subprojectId/user-stories/:userStoryId/edit" element={<EditUserStoriesWrapper />} />
          <Route path="/projects/:id/user-stories/:userStoryId/tasks/add" element={<AddTask onBack={() => navigate(-1)} />} />
          <Route path="/projects/:id/subprojects/:subprojectId/user-stories/:userStoryId/tasks/add" element={<AddTask onBack={() => navigate(-1)} />} />
          <Route path="/projects/:id/user-stories/:userStoryId/tasks/:taskId/view" element={<ViewTaskWrapper />} />
          <Route path="/projects/:id/subprojects/:subprojectId/user-stories/:userStoryId/tasks/:taskId/view" element={<ViewTaskWrapper />} />
          <Route path="/projects/:id/user-stories/:userStoryId/tasks/:taskId/edit" element={<EditTask onBack={() => navigate(-1)} />} />
          <Route path="/projects/:id/subprojects/:subprojectId/user-stories/:userStoryId/tasks/:taskId/edit" element={<EditTask onBack={() => navigate(-1)} />} />
          <Route path="/projects/:id/user-stories/:userStoryId/tasks/:taskId/subtasks/add" element={<AddSubtask onBack={() => navigate(-1)} />} />
          <Route path="/projects/:id/subprojects/:subprojectId/user-stories/:userStoryId/tasks/:taskId/subtasks/add" element={<AddSubtask onBack={() => navigate(-1)} />} />
          <Route path="/projects/:id/user-stories/:userStoryId/tasks/:taskId/subtasks/:subTaskId/view" element={<ViewSubTask onBack={() => navigate(-1)} />} />
          <Route path="/projects/:id/subprojects/:subprojectId/user-stories/:userStoryId/tasks/:taskId/subtasks/:subTaskId/view" element={<ViewSubTask onBack={() => navigate(-1)} />} />
          <Route path="/projects/:id/user-stories/:userStoryId/tasks/:taskId/subtasks/:subTaskId/edit" element={<EditSubTask onBack={() => navigate(-1)} />} />
          <Route path="/projects/:id/subprojects/:subprojectId/user-stories/:userStoryId/tasks/:taskId/subtasks/:subTaskId/edit" element={<EditSubTask onBack={() => navigate(-1)} />} />
          <Route path="/issues" element={<Issues />} />
          <Route path="/issues/test-cases" element={<Issues />} />
          <Route path="/issues/add" element={<AddIssue />} />
          <Route path="/issues/test-cases/add" element={<AddTestCase />} />
          <Route path="/issues/test-cases/:testCaseId/view" element={<ViewTestCase />} />
          <Route path="/issues/test-cases/:testCaseId/edit" element={<EditTestCase />} />
          <Route path="/issues/:issueId/view" element={<ViewIssue />} />
          <Route path="/issues/:issueId/edit" element={<EditIssue />} />

          {/* Customer Routes */}
          <Route path="/tenants/add" element={
            <AddTenant onBack={() => navigate("/tenants")} onAddTenant={handleAddTenant} />
          } />
          
          {/* Business Lead Routes */}
          <Route path="/business-leads/add" element={
            <AddBusinessLead onBack={handleBackToLeads} onAddLead={handleAddLead} />
          } />
          <Route path="/business-leads/:id/view" element={
            <ViewBusinessLeadWrapper onBack={handleBackFromViewLead} onEditLead={handleEditLeadClick} onAddFollowup={handleAddFollowupClick} />
          } />
          <Route path="/business-leads/:id/edit" element={
            <EditBusinessLeadWrapper onBack={handleBackFromEditLead} onUpdateLead={handleAddLead} />
          } />

          {/* Real Estate Lead Routes */}
          <Route path="/realestate-leads/add" element={
            <AddRealestateLead onBack={handleBackToLeads} onAddLead={handleAddLead} />
          } />
          <Route path="/realestate-leads/:id/view" element={
            <ViewRealestateLeadWrapper onBack={handleBackFromViewLead} onEditLead={handleEditLeadClick} />
          } />
          <Route path="/realestate-leads/:id/edit" element={
            <EditRealestateLeadWrapper onBack={handleBackFromEditLead} onUpdateLead={handleAddLead} />
          } />

          {/* Job Lead Routes */}
          <Route path="/job-leads/add" element={
            <AddJobLead onBack={handleBackToLeads} onAddLead={handleAddLead} />
          } />
          <Route path="/job-leads/:id/view" element={
            <ViewJobLeadWrapper onBack={handleBackFromViewLead} onEditLead={handleEditLeadClick} />
          } />
          <Route path="/job-leads/:id/edit" element={
            <EditJobLeadWrapper onBack={handleBackFromEditLead} onUpdateLead={handleAddLead} />
          } />

          {/* Internship Lead Routes */}
          <Route path="/internship-leads/add" element={
            <AddInternshipLead onBack={handleBackToLeads} onAddLead={handleAddLead} />
          } />
          <Route path="/internship-leads/:id/view" element={
            <ViewInternshipLeadWrapper onBack={handleBackFromViewLead} onEditLead={handleEditLeadClick} />
          } />
          <Route path="/internship-leads/:id/edit" element={
            <EditInternshipLeadWrapper onBack={handleBackFromEditLead} onUpdateLead={handleAddLead} />
          } />
          
          {/* Training Lead Routes */}
          <Route path="/training-leads/add" element={
            <AddTrainingLead onBack={handleBackToLeads} onAddLead={handleAddLead} courses={courses} />
          } />
          <Route path="/training-leads/:id/view" element={
            <ViewTrainingLeadWrapper onBack={handleBackFromViewLead} onEditLead={handleEditLeadClick} onAddFollowup={handleAddFollowupClick} />
          } />
          <Route path="/training-leads/:id/edit" element={
            <EditTrainingLeadWrapper courses={courses} onBack={handleBackFromEditLead} onUpdateLead={handleAddLead} />
          } />
          <Route path="/tenants/:id/view" element={
            <ViewTenantWrapper
              onBack={handleBackFromViewLead}
              onEditLead={handleEditLeadClick}
              onAddFollowup={handleAddFollowupClick}
              onPageTitleReady={setTenantViewTitle}
            />
          } />
          <Route path="/tenants/:id/edit" element={
            <EditTenantWrapper onBack={handleBackFromEditTenant} onUpdate={handleUpdateLead} />
          } />

          {/* Account Routes */}
          <Route
            path="/accounts/add"
            element={
              <AddAccounts
                onBack={() => navigate(-1)}
                onCreated={handleUpdateLead}
              />
            }
          />
          <Route
            path="/tenants/:clientId/social-accounts/add"
            element={<AddCustomerSocialAccountWrapper onAddAccount={handleUpdateLead} />}
          />
          <Route
            path="/tenants/:clientId/social-accounts/:id/view"
            element={<ViewSocialAccountWrapper />}
          />
          <Route
            path="/tenants/:clientId/social-accounts/:id/edit"
            element={<EditSocialAccountWrapper onUpdate={handleUpdateLead} />}
          />
          <Route path="/accounts/:id/view" element={
            <ViewAccountWrapper />
          } />
          <Route path="/accounts/:id/edit" element={
            <EditAccountWrapper onUpdate={handleUpdateLead} />
          } />

          {/* User Routes */}
          <Route path="/users/org-chart" element={
            <OrgChart
              api={staffWorkApi}
              profileLink={(id) => `/users/${id}/view`}
              editLink={(id) => `/users/${id}/edit`}
            />
          } />
          <Route path="/users/add" element={
            <AddUser onBack={handleBackToUsers} onAddUser={handleAddUser} />
          } />
          <Route path="/users/:id/view" element={
            <ViewUserWrapper onBack={handleBackFromViewUser} onEditUser={handleEditUser} />
          } />
          <Route path="/users/:id/edit" element={
            <EditUserWrapper onBack={handleBackFromEditUser} onUpdate={handleUpdateUser} />
          } />

          {/* Support Contact Routes */}
          <Route path="/support-contacts/staff/add" element={
            <AddOfficeStaff
              onBack={() => navigate("/support-contacts/staff")}
              onAddStaff={() => {
                fetchSupportContactsData();
                navigate("/support-contacts/staff");
              }}
            />
          } />
          <Route path="/support-contacts/staff/:id/edit" element={
            <AddOfficeStaff
              onBack={() => navigate("/support-contacts/staff")}
              onAddStaff={() => {
                fetchSupportContactsData();
                navigate("/support-contacts/staff");
              }}
            />
          } />
          <Route path="/support-contacts/service/add" element={
            <AddOfficeService
              onBack={() => navigate("/support-contacts/service")}
              onAddService={() => {
                fetchSupportContactsData();
                navigate("/support-contacts/service");
              }}
            />
          } />
          <Route path="/support-contacts/service/:id/edit" element={
            <AddOfficeService
              onBack={() => navigate("/support-contacts/service")}
              onAddService={() => {
                fetchSupportContactsData();
                navigate("/support-contacts/service");
              }}
            />
          } />
          <Route path="/support-contacts/add" element={
            <AddSupportContact 
              onBack={() => navigate("/support-contacts/staff")} 
              onAddContact={(contactType) => {
                fetchSupportContactsData();
                const tab = (contactType || "service").toLowerCase();
                navigate(tab === "staff" ? "/support-contacts/staff" : "/support-contacts/service");
              }} 
            />
          } />
          <Route path="/support-contacts/staff/:id/view" element={
            <ViewOfficeStaffWrapper
              onBack={() => navigate("/support-contacts/staff")}
              onEdit={(id) => navigate(`/support-contacts/staff/${id}/edit`)}
            />
          } />
          <Route path="/support-contacts/:id/view" element={
            <ViewSupportContactWrapper
              onBack={() => navigate("/support-contacts/staff")}
              onEdit={(id, isOfficeService) => navigate(isOfficeService ? `/support-contacts/service/${id}/edit` : `/support-contacts/${id}/edit`)}
            />
          } />
          <Route path="/support-contacts/:id/edit" element={
            <AddSupportContact 
              onBack={() => navigate("/support-contacts/staff")} 
              onAddContact={(contactType) => {
                fetchSupportContactsData();
                const tab = (contactType || "service").toLowerCase();
                navigate(tab === "staff" ? "/support-contacts/staff" : "/support-contacts/service");
              }} 
            />
          } />

          {/* Other Routes */}
          <Route path="/my-details" element={
            <MyDetails onBack={handleBackToDashboard} permissionsVersion={permissionsVersion} />
          } />
          <Route path="/change-password" element={
            <ChangePassword onBack={handleBackToDashboard} />
          } />
          <Route path="/settings" element={<Settings />} />
          <Route path="/permissions" element={
            <Permissions onBack={handleBackToDashboard} onPermissionsUpdated={handlePermissionsUpdated} />
          } />
          <Route path="/followups/add" element={
            <AddFollowupWrapper onBack={handleBackFromAddFollowup} />
          } />
          <Route path="/generate-quote" element={<QuoteGeneratorPage />} />
          <Route path="/forgot-password" element={
            <ForgotPassword />
          } />
          <Route path="/reset-password" element={
            <ResetPassword />
          } />
          <Route path="*" element={<Navigate to={getDefaultLandingPath(getCurrentUserRole())} replace />} />
        </Routes>
        </div>
      </main>
    </div>
  );
}

// Wrapper components to extract URL params and query strings
function AddLeadWrapper({ courses, onBack, onAddLead }) {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const type = params.get('type') || 'lead';
  
  // Route to AddTrainingLead if type is training-lead, otherwise AddBusinessLead
  if (type === "training-lead") {
    return <AddTrainingLead courses={courses} onBack={onBack} onAddLead={onAddLead} />;
  }
  
  return <AddBusinessLead onBack={onBack} onAddLead={onAddLead} />;
}

function ViewBusinessLeadWrapper({ onBack, onEditLead, onAddFollowup }) {
  const { id } = useParams();
  return (
    <ViewBusinessLead
      leadId={parseInt(id, 10)}
      onBack={onBack}
      onEditLead={onEditLead}
      onAddFollowup={onAddFollowup}
    />
  );
}

function ViewTrainingLeadWrapper({ onBack, onEditLead, onAddFollowup }) {
  const { id } = useParams();
  return (
    <ViewTrainingLead
      leadId={parseInt(id, 10)}
      onBack={onBack}
      onEditLead={onEditLead}
      onAddFollowup={onAddFollowup}
    />
  );
}

function ViewTenantWrapper({ onBack, onEditLead, onAddFollowup, onPageTitleReady }) {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const backTo = location.state?.backTo;
  return (
    <ViewTenant
      leadId={parseInt(id, 10)}
      onBack={backTo ? () => navigate(backTo) : onBack}
      backLabel={backTo ? location.state?.backLabel : undefined}
      onEditLead={onEditLead}
      onAddFollowup={onAddFollowup}
      onPageTitleReady={onPageTitleReady}
    />
  );
}

function ViewJobLeadWrapper({ onBack, onEditLead }) {
  const { id } = useParams();
  return <ViewJobLead leadId={parseInt(id)} onBack={onBack} onEditLead={onEditLead} />;
}

function ViewRealestateLeadWrapper({ onBack, onEditLead }) {
  const { id } = useParams();
  return <ViewRealestateLead leadId={parseInt(id)} onBack={onBack} onEditLead={onEditLead} />;
}

function EditRealestateLeadWrapper({ onBack, onUpdateLead }) {
  const { id } = useParams();
  return <EditRealestateLead leadId={parseInt(id)} onBack={onBack} onUpdateLead={onUpdateLead} />;
}

function ViewInternshipLeadWrapper({ onBack, onEditLead }) {
  const { id } = useParams();
  return <ViewInternshipLead leadId={parseInt(id)} onBack={onBack} onEditLead={onEditLead} />;
}

function EditInternshipLeadWrapper({ onBack, onUpdateLead }) {
  const { id } = useParams();
  return (
    <EditInternshipLead
      leadId={parseInt(id)}
      onBack={onBack}
      onUpdateLead={onUpdateLead}
    />
  );
}

function EditBusinessLeadWrapper({ onBack, onUpdateLead }) {
  const { id } = useParams();
  return <EditBusinessLead leadId={parseInt(id)} onBack={onBack} onUpdateLead={onUpdateLead} />;
}

function EditJobLeadWrapper({ onBack, onUpdateLead }) {
  const { id } = useParams();
  return <EditJobLead leadId={parseInt(id)} onBack={onBack} onUpdateLead={onUpdateLead} />;
}

function EditTrainingLeadWrapper({ courses, onBack, onUpdateLead }) {
  const { id } = useParams();
  return <EditTrainingLead leadId={parseInt(id)} courses={courses} onBack={onBack} onUpdateLead={onUpdateLead} />;
}

function EditTenantWrapper({ onBack, onUpdate }) {
  const { id } = useParams();
  return <EditTenant clientId={parseInt(id)} onBack={onBack} onUpdate={onUpdate} />;
}

function AddAccountWrapper({ onAddAccount }) {
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = location.state?.returnTo || "/tenants";

  return (
    <AddAccounts
      onBack={() => navigate(returnTo)}
      onCreated={() => {
        if (onAddAccount) onAddAccount();
      }}
    />
  );
}

function AddCustomerSocialAccountWrapper({ onAddAccount }) {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = location.state?.returnTo || `/tenants/${clientId}/view`;

  // If opened without tenantTitle (e.g. refresh), load tenant name for the page header
  useEffect(() => {
    if (location.state?.tenantTitle?.name || !clientId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await axiosInstance.get(`/client/${clientId}`);
        const client = res.data?.client || res.data || {};
        if (cancelled) return;
        const typeRaw = String(client.Customer_Type || client.customer_type || "").trim().toLowerCase();
        const isOrg =
          typeRaw === "organization" ||
          typeRaw === "organisation" ||
          typeRaw === "org";
        const company = String(client.Company_Name || client.company_name || "").trim();
        const first = String(client.First_Name || "").trim();
        const last = String(client.Last_Name || "").trim();
        const person = [first, last].filter(Boolean).join(" ");
        const name = isOrg
          ? company || person || "Client"
          : person || company || "Client";
        navigate(location.pathname, {
          replace: true,
          state: {
            ...(location.state || {}),
            returnTo,
            clientId,
            tenantTitle: {
              name,
              id: client.client_id ?? client.Client_Id ?? clientId,
              status: client.Status || client.status || "",
            },
          },
        });
      } catch {
        /* keep fallback title */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [clientId, location.pathname, location.state, navigate, returnTo]);

  return (
    <AddSocialaccount
      initialClientId={clientId}
      onBack={() => navigate(returnTo)}
      onAddAccount={() => {
        if (onAddAccount) onAddAccount();
      }}
    />
  );
}

function EditAccountWrapper({ onUpdate }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = location.state?.returnTo || "/accounts";
  return (
    <EditAccounts
      accountId={parseInt(id)}
      onBack={() => navigate(returnTo)}
      onUpdate={onUpdate}
    />
  );
}

function ViewAccountWrapper() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = location.state?.returnTo || "/accounts";

  const handleEdit = (accountId) => {
    navigate(`/accounts/${accountId}/edit`, { state: { returnTo } });
  };

  return (
    <ViewAccounts
      accountId={parseInt(id)}
      onBack={() => navigate(returnTo)}
      onEditAccount={handleEdit}
    />
  );
}

function EditSocialAccountWrapper({ onUpdate }) {
  const { id, clientId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = location.state?.returnTo || `/tenants/${clientId}/view`;
  return (
    <EditSocialaccount
      accountId={parseInt(id)}
      onBack={() => navigate(returnTo)}
      onUpdate={onUpdate}
    />
  );
}

function ViewSocialAccountWrapper() {
  const { id, clientId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = location.state?.returnTo || `/tenants/${clientId}/view`;

  const handleEdit = (accountId) => {
    navigate(`/tenants/${clientId}/social-accounts/${accountId}/edit`, { state: { returnTo } });
  };

  return (
    <ViewSocialaccount
      accountId={parseInt(id)}
      onBack={() => navigate(returnTo)}
      onEditAccount={handleEdit}
    />
  );
}

function ViewProjectWrapper({ onBack, onEditProject, onPageTitleReady }) {
  const { id } = useParams();
  return <ViewProject projectId={id} onBack={onBack} onEditProject={onEditProject} onPageTitleReady={onPageTitleReady} />;
}

function ViewProjectPaymentWrapper() {
  const { id } = useParams();
  const navigate = useNavigate();
  return (
    <ViewProjectPayment
      paymentId={parseInt(id)}
      onBack={() => navigate(-1)}
    />
  );
}

function ViewProjectSubscriptionWrapper() {
  const { id } = useParams();
  const navigate = useNavigate();
  return (
    <ViewProjectSubscription
      subscriptionId={parseInt(id, 10)}
      onBack={() => navigate(-1)}
    />
  );
}

function EditProjectSubscriptionWrapper() {
  const { id } = useParams();
  const navigate = useNavigate();
  return (
    <EditProjectSubscription
      subscriptionId={parseInt(id, 10)}
      onBack={() => navigate(-1)}
    />
  );
}

function EditProjectPaymentWrapper() {
  const { id } = useParams();
  const navigate = useNavigate();
  return (
    <EditProjectPayment
      paymentId={parseInt(id)}
      onBack={() => navigate(-1)}
    />
  );
}

function AddProjectPaymentPageWrapper() {
  return <AddProjectPaymentPage />;
}

function EditProjectWrapper({ onBack, onUpdateProject }) {
  return <EditProject onBack={onBack} onUpdateProject={onUpdateProject} />;
}

function AddSubprojectWrapper({ navigate }) {
  const { id } = useParams();
  return <AddSubproject onBack={() => navigate(-1)} />;
}

function ViewSubprojectWrapper({ navigate }) {
  const { id, subprojectId } = useParams();
  return (
    <ViewSubproject
      onBack={() => navigate(-1)}
      onEditSubproject={() => navigate(`/projects/${id}/subprojects/${subprojectId}/edit`)}
    />
  );
}

function EditSubprojectWrapper({ navigate, fetchProjects }) {
  const { id, subprojectId } = useParams();
  return (
    <EditSubproject
      onBack={() => navigate(-1)}
      onUpdateSubproject={() => {
        if (fetchProjects) fetchProjects();
      }}
    />
  );
}

function ViewUserStoriesWrapper() {
  const navigate = useNavigate();
  return (
    <ViewUserStories
      onBack={() => navigate("/user-stories", { state: { resetUserStoryFilters: true } })}
    />
  );
}

function EditUserStoriesWrapper() {
  const navigate = useNavigate();
  return (
    <EditUserStories
      onBack={() => navigate("/user-stories", { state: { resetUserStoryFilters: true } })}
    />
  );
}

function ViewTaskWrapper() {
  const navigate = useNavigate();
  return <ViewTask onBack={() => navigate(-1)} />;
}

function ViewUserWrapper({ onBack, onEditUser }) {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const backTo = location.state?.backTo;
  return (
    <ViewUser
      userId={parseInt(id)}
      onBack={backTo ? () => navigate(backTo) : onBack}
      onEditUser={onEditUser}
    />
  );
}

function EditUserWrapper({ onBack, onUpdate }) {
  const { id } = useParams();
  return <EditUser userId={parseInt(id)} onBack={onBack} onUpdate={onUpdate} />;
}

function ViewSupportContactWrapper({ onBack, onEdit }) {
  const { id } = useParams();
  return (
    <ViewSupportContact
      contactId={parseInt(id)}
      onBack={onBack}
      onEdit={onEdit}
    />
  );
}

function ViewOfficeStaffWrapper({ onBack, onEdit }) {
  const { id } = useParams();
  return (
    <ViewOfficeStaff
      staffId={id}
      onBack={onBack}
      onEdit={onEdit}
    />
  );
}

function AddFollowupWrapper({ onBack }) {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const leadId = params.get('lead_id');
  const clientId = params.get('client_id');
  const trainingLeadId = params.get('training_lead_id');
  const realestateLeadId = params.get('realestate_lead_id');
  const id = leadId || clientId || trainingLeadId || realestateLeadId;
  const type = clientId ? 'client' : trainingLeadId ? 'training-lead' : realestateLeadId ? 'realestate-lead' : 'lead';
  return <AddFollowup id={parseInt(id)} type={type} onBack={onBack} />;
}

// Chooses between the staff CRM app and the customer portal based on the
// authenticated account type stored at login.
function RootApp() {
  migrateLegacyAuthToSession();
  const isCustomer =
    getAuthItem("account_type") === "client" &&
    !!getAuthItem("access_token");
  if (isCustomer) {
    return <TenantPortal />;
  }
  return <AppContent />;
}

// A native submit of a form without named fields (or an empty query string) leaves a bare "?"
// on the URL, e.g. /tenants/9/edit?. location.search is "" in that case, so check the href.
function StripEmptyQuery() {
  const location = useLocation();
  useEffect(() => {
    const { href } = window.location;
    const bare = href.split("#")[0];
    if (bare.endsWith("?")) {
      window.history.replaceState(window.history.state, "", href.replace(/\?(?=#|$)/, ""));
    }
  }, [location]);
  return null;
}

// Main App Component with Router
function App() {
  const ctxPath = getCtxPath();
  return (
    <BrowserRouter basename={ctxPath || undefined}>
      <StripEmptyQuery />
      <ConfirmProvider>
        <RootApp />
        <InstallPwaBanner />
      </ConfirmProvider>
    </BrowserRouter>
  );
}

export default App;
